import Phaser from 'phaser';

/**
 * VFX Manager — central place for all visual effects.
 *
 * Two categories:
 *  - Sprite-based: animated sprite sheet played once at a position, auto-destroyed
 *  - Particle-based: burst emitter exploded once, auto-destroyed after lifespan
 */
export class VFXManager {
  constructor(private scene: Phaser.Scene) {}


  /**
   * Frostbolt projectile: waterball orb travels from mage right-center to target.
   * On arrival plays the blizzard impact burst above the target.
   */
  playFrostbolt(fromX: number, fromY: number, toX: number, toY: number): void {
    const texKey = this.scene.textures.exists('effect_frostball') ? 'effect_frostball' : 'frost_effect';
    const animKey = texKey === 'frost_effect' ? 'frost_anim' : undefined;
    const sprite = this.scene.add.sprite(fromX + 36, fromY, texKey)
      .setDisplaySize(36, 36).setDepth(20).setOrigin(0.5);
    if (animKey && this.scene.anims.exists(animKey)) sprite.play(animKey);
    this.scene.tweens.add({
      targets: sprite,
      x: toX,
      y: toY,
      duration: 300,
      ease: 'Linear',
      onComplete: () => {
        sprite.destroy();
        this.playBlizzard(toX, toY);
      },
    });
  }

  /** Blizzard impact burst above target — waterball frames 14+15 */
  playBlizzard(x: number, y: number): void {
    this.playFxSprite(x, y - 28, 'effect_blizzard', 'fx_blizzard', 96, 0.5);
  }

  /** Green glow on healed hero — bottom-anchored at feet (origin 0.5, 1.0) */
  playHealGlow(x: number, y: number): void {
    if (!this.scene.textures.exists('heal_effect')) return;
    const sprite = this.scene.add.sprite(x, y, 'heal_effect')
      .setDepth(25).setOrigin(0.5, 1.0).setDisplaySize(64, 64);
    if (this.scene.anims.exists('heal_anim')) {
      sprite.play('heal_anim');
      sprite.once('animationcomplete', () => sprite.destroy());
    } else {
      this.scene.tweens.add({ targets: sprite, alpha: 0, duration: 180, onComplete: () => sprite.destroy() });
    }
  }

  /** Gold coins fly from (srcX, srcY) to (dstX, dstY) along arcs — used after
   *  a Horde clear to visually deposit the loot into the HUD gold counter.
   *  `count` coins, spawned `stagger` ms apart, each tweened 700 ms along a
   *  parabolic arc (slight up + over). `onArrive` fires for each coin when it
   *  lands so the caller can pulse the gold counter. */
  playGoldFlightTo(srcX: number, srcY: number, dstX: number, dstY: number, opts: {
    count?: number;
    stagger?: number;
    onArrive?: () => void;
  } = {}): void {
    const count = opts.count ?? 14;
    const stagger = opts.stagger ?? 55;
    const onArrive = opts.onArrive;
    for (let i = 0; i < count; i++) {
      this.scene.time.delayedCall(i * stagger, () => {
        // Small jitter at source so the coin stream doesn't stack.
        const jitterX = (Math.random() - 0.5) * 40;
        const jitterY = (Math.random() - 0.5) * 20;
        const sx = srcX + jitterX;
        const sy = srcY + jitterY;
        const coin = this.scene.add.circle(sx, sy, 4, 0xfbbf24, 1)
          .setStrokeStyle(1, 0xfff7b0, 0.9)
          .setDepth(99)
          .setBlendMode(Phaser.BlendModes.ADD);
        // Parabolic arc: go UP and toward the destination in one tween using
        // a Bezier-like bias — we fake this with a two-stage tween (up + over,
        // then drop to destination). Simpler: single tween with ease that
        // goes up-and-across via easing on y.
        const peakY = Math.min(sy, dstY) - 80 - Math.random() * 40;
        // Phase 1: arc to peak above midpoint.
        const midX = (sx + dstX) / 2 + (Math.random() - 0.5) * 40;
        this.scene.tweens.add({
          targets: coin,
          x: midX, y: peakY,
          duration: 260,
          ease: 'Cubic.easeOut',
          onComplete: () => {
            if (!coin.active) return;
            this.scene.tweens.add({
              targets: coin,
              x: dstX, y: dstY,
              scale: 0.6,
              duration: 360,
              ease: 'Cubic.easeIn',
              onComplete: () => {
                coin.destroy();
                if (onArrive) onArrive();
              },
            });
          },
        });
      });
    }
  }

  /** Gold-coin shower — used when the Horde of Goblins reward fires. Two
   *  staggered bursts of gold sparks erupt from (x,y), arc up and outward,
   *  then fall under gravity. Reads as "the goblin hoard explodes into your
   *  pockets" after a horde clear. */
  playGoldShower(x: number, y: number): void {
    const burst = (delay: number): void => {
      this.scene.time.delayedCall(delay, () => {
        this.burstParticles(x, y, 'projectile', 26, {
          speed: { min: 120, max: 280 },
          angle: { min: 220, max: 320 }, // upward fan, then gravity pulls them down
          scale: { start: 0.7, end: 0 },
          tint: [0xfbbf24, 0xfde047, 0xeab308, 0xfff7b0],
          lifespan: 1200,
          gravityY: 420,
        });
      });
    };
    burst(0);
    burst(160);
    burst(320);
    // Brief golden flash at the source
    const flash = this.scene.add.circle(x, y, 38, 0xfbbf24, 0.45)
      .setDepth(60).setBlendMode(Phaser.BlendModes.ADD);
    this.scene.tweens.add({
      targets: flash, scale: { from: 0.6, to: 2.6 }, alpha: { from: 0.55, to: 0 },
      duration: 700, ease: 'Cubic.easeOut',
      onComplete: () => flash.destroy(),
    });
  }

  /** Small spark burst on hit */
  playHitSpark(x: number, y: number): void {
    this.burstParticles(x, y, 'projectile', 6, {
      speed: { min: 60, max: 140 },
      angle: { min: 0, max: 360 },
      scale: { start: 0.35, end: 0 },
      tint: [0xffffff, 0xfbbf24],
      lifespan: 220,
    });
  }

  /** Poof explosion on enemy death */
  playDeathPoof(x: number, y: number): void {
    this.burstParticles(x, y, 'projectile', 14, {
      speed: { min: 25, max: 110 },
      angle: { min: 0, max: 360 },
      scale: { start: 0.7, end: 0 },
      tint: [0xd1d5db, 0x6b7280, 0xffffff],
      lifespan: 420,
    });
  }

  /** Cyrith auto-attack on-hit — Combat FX row 28 (small yellow/blue spark).
   *  Plays once at the target enemy's body. Smaller display size (40 px) so
   *  fast attack chains don't dominate the screen. Falls back silently if
   *  the texture or anim isn't loaded — auto-attacks still register hits
   *  via damage numbers + sprite flash. */
  playCyrithAutoStrike(x: number, y: number): void {
    if (!this.scene.textures.exists('effect_combat_fx')
        || !this.scene.anims.exists('fx_cyrith_auto_strike')) return;
    const spark = this.scene.add.sprite(x, y, 'effect_combat_fx')
      .setDisplaySize(40, 40)
      .setOrigin(0.5, 0.5)
      .setDepth(18)
      .setBlendMode(Phaser.BlendModes.ADD);
    spark.play('fx_cyrith_auto_strike');
    spark.once('animationcomplete', () => spark.destroy());
  }

  /** Cyrith Storm Bolt — distinct projectile. Pale-blue jagged bolt that
   *  travels from caster to target with a slight zigzag. Distinct from
   *  Telurion's storm-kite missile (which Cyrith was reusing). */
  playCyrithStormBolt(fromX: number, fromY: number, toX: number, toY: number): void {
    const angle = Math.atan2(toY - fromY, toX - fromX);
    const dist = Math.hypot(toX - fromX, toY - fromY);
    const duration = Math.max(140, dist * 0.30);
    // Jagged bolt body — drawn as a Graphics line that follows the
    // projectile's tween. Each frame redraws a slightly-different zigzag
    // so the bolt visibly twitches as it travels.
    const head = this.scene.add.circle(fromX, fromY, 5, 0xbfeeff, 1)
      .setDepth(18).setBlendMode(Phaser.BlendModes.ADD);
    const trail = this.scene.add.graphics().setDepth(17).setBlendMode(Phaser.BlendModes.ADD);
    const update = () => {
      trail.clear();
      trail.lineStyle(2.5, 0xa8e0ff, 0.9);
      // Draw a 4-segment zigzag from caster to current head position.
      const dx = head.x - fromX, dy = head.y - fromY;
      const segs = 4;
      const px = -Math.sin(angle), py = Math.cos(angle);                 // perpendicular unit
      trail.beginPath();
      trail.moveTo(fromX, fromY);
      for (let i = 1; i < segs; i++) {
        const t = i / segs;
        const offset = (i % 2 === 0 ? 1 : -1) * (6 + Math.random() * 4);
        trail.lineTo(fromX + dx * t + px * offset, fromY + dy * t + py * offset);
      }
      trail.lineTo(head.x, head.y);
      trail.strokePath();
    };
    this.scene.tweens.add({
      targets: head, x: toX, y: toY, duration, ease: 'Linear',
      onUpdate: update,
      onComplete: () => {
        head.destroy();
        trail.destroy();
        // Brief on-impact flash — small white circle that pops + fades.
        // Replaces the 4-prong "snowflake" star burst the user wanted gone.
        const flash = this.scene.add.circle(toX, toY, 8, 0xeaffff, 0.9)
          .setDepth(18).setBlendMode(Phaser.BlendModes.ADD);
        this.scene.tweens.add({
          targets: flash, alpha: 0, scale: { from: 1, to: 2.4 },
          duration: 200, onComplete: () => flash.destroy(),
        });
      },
    });
  }

  /** Cyrith Arc chain — short jagged lightning arc between two points.
   *  Used by Arc Strike's chain hit and Static Field per-tick crackles.
   *  Single quick draw + fade, no projectile motion. */
  playCyrithArcChain(fromX: number, fromY: number, toX: number, toY: number): void {
    const arc = this.scene.add.graphics().setDepth(18).setBlendMode(Phaser.BlendModes.ADD);
    const dx = toX - fromX, dy = toY - fromY;
    const angle = Math.atan2(dy, dx);
    const px = -Math.sin(angle), py = Math.cos(angle);
    arc.lineStyle(2, 0xbfeeff, 1.0);
    arc.beginPath();
    arc.moveTo(fromX, fromY);
    const segs = 5;
    for (let i = 1; i < segs; i++) {
      const t = i / segs;
      const offset = (i % 2 === 0 ? 1 : -1) * (4 + Math.random() * 5);
      arc.lineTo(fromX + dx * t + px * offset, fromY + dy * t + py * offset);
    }
    arc.lineTo(toX, toY);
    arc.strokePath();
    this.scene.tweens.add({
      targets: arc, alpha: 0, duration: 220, ease: 'Cubic.easeIn',
      onComplete: () => arc.destroy(),
    });
  }

  /** Cyrith Static Field — Paralyzed status sprite wrapped on the golem.
   *  Replaces the previous dashed-ring graphic. The 7-frame Paralyzed
   *  spritesheet plays as a looping aura that hugs Vorrak's body, giving
   *  the field its "electric shield" read instead of a runic ground ring.
   *  Returns a follow/destroy handle for the caller. */
  spawnCyrithFieldShield(x: number, y: number): {
    container: Phaser.GameObjects.Container;
    follow(targetX: number, targetY: number): void;
    destroy(): void;
  } {
    const container = this.scene.add.container(x, y).setDepth(2.05);
    // Two stacked Paralyzed sprites — one big (covers the body), one
    // smaller (intensifies the inner crackle). Both use ADD blend so the
    // crackles glow against the dim battlefield. Sprite is anchored at
    // 0, -28 inside the container so it sits over the golem's torso
    // (his feet are at the container origin via the slot positioning).
    const hasSprite = this.scene.textures.exists('effect_paralyzed');
    const hasAnim = this.scene.anims.exists('fx_paralyzed');
    const big = hasSprite ? this.scene.add.sprite(0, -32, 'effect_paralyzed')
        .setDisplaySize(120, 120).setOrigin(0.5, 0.5).setBlendMode(Phaser.BlendModes.ADD).setAlpha(0.95)
      : null;
    const small = hasSprite ? this.scene.add.sprite(0, -28, 'effect_paralyzed')
        .setDisplaySize(72, 72).setOrigin(0.5, 0.5).setBlendMode(Phaser.BlendModes.ADD).setAlpha(0.85)
      : null;
    if (big && hasAnim) big.play('fx_paralyzed');
    // Offset the smaller sprite's anim so the two layers crackle out of
    // phase — reads as more chaotic electricity, not a duplicated frame.
    if (small && hasAnim) {
      small.play('fx_paralyzed');
      small.anims.setProgress(0.4);
    }
    if (big) container.add(big);
    if (small) container.add(small);
    return {
      container,
      follow: (tx: number, ty: number) => container.setPosition(tx, ty),
      destroy: () => {
        try { container.destroy(); } catch { /* ignore */ }
      },
    };
  }

  /** Lootfi Forgefire Strikes — orange ember ring + small spark burst on the
   *  cleave hit. Plays at the primary target so neighboring enemies caught
   *  in the 120 px cleave radius read as collateral. */
  playLootfiForgefireCleave(x: number, y: number): void {
    this.expandingRing(x, y, 0xff8030, 120, 280);
    this.burstParticles(x, y, 'projectile', 8, {
      speed: { min: 60, max: 140 },
      angle: { min: 0, max: 360 },
      lifespan: 320,
      tint: 0xff8030,
    });
  }

  /** Crimson arrival puff + shockring for elite spawns — "something dangerous entered the fight". */
  playEliteSpawn(x: number, y: number): void {
    this.expandingRing(x, y, 0xef4444, 60, 420);
    const flash = this.scene.add.circle(x, y - 24, 18, 0xef4444, 0.55)
      .setDepth(19).setBlendMode(Phaser.BlendModes.ADD);
    this.scene.tweens.add({
      targets: flash, scale: { from: 0.6, to: 2.2 }, alpha: { from: 0.55, to: 0 },
      duration: 520, ease: 'Cubic.easeOut',
      onComplete: () => flash.destroy(),
    });
    this.burstParticles(x, y, 'projectile', 12, {
      speed: { min: 40, max: 140 },
      angle: { min: 220, max: 320 },
      scale: { start: 0.6, end: 0 },
      tint: [0xef4444, 0x991b1b, 0xf97316, 0x7f1d1d],
      lifespan: 540,
    });
  }

  /** Bigger shockwave + embers when an elite dies — punchier than a regular death poof. */
  playEliteDeath(x: number, y: number): void {
    this.expandingRing(x, y, 0xef4444, 80, 520);
    this.scene.time.delayedCall(120, () => this.expandingRing(x, y, 0x991b1b, 60, 420));
    this.burstParticles(x, y, 'projectile', 20, {
      speed: { min: 60, max: 200 },
      angle: { min: 0, max: 360 },
      scale: { start: 0.7, end: 0 },
      tint: [0xef4444, 0xf97316, 0x991b1b, 0xfbbf24],
      lifespan: 560,
    });
    // Upward embers
    this.burstParticles(x, y - 6, 'projectile', 8, {
      speed: { min: 30, max: 100 },
      angle: { min: 240, max: 300 },
      scale: { start: 0.5, end: 0 },
      tint: [0xef4444, 0xfbbf24],
      lifespan: 720,
      gravityY: -90,
    });
  }

  /** Archivist — pale frost motes rising from his feet every ~500ms while he lives.
   *  Signals "arcane caster" and gives the dark-robed wizard extra contrast on dark maps. */
  playArchivistAuraTick(x: number, y: number): void {
    this.burstParticles(x, y - 4, 'projectile', 3, {
      speed: { min: 15, max: 45 },
      angle: { min: 240, max: 300 },
      scale: { start: 0.45, end: 0 },
      tint: [0x8ad4ff, 0xa7c7ff, 0xc4a8ff, 0xd6e6ff],
      lifespan: 1100,
      gravityY: -30,
    });
  }

  /** White Claw (wolf boss) — crimson ember tick called every ~600ms while he lives.
   *  A bit more aggressive than the elite ember drift to read as a "leader" presence. */
  playWolfBossAuraTick(x: number, y: number): void {
    this.burstParticles(x, y - 10, 'projectile', 4, {
      speed: { min: 20, max: 60 },
      angle: { min: 210, max: 330 },
      scale: { start: 0.55, end: 0 },
      tint: [0xef4444, 0x991b1b, 0xfbbf24, 0x7f1d1d],
      lifespan: 900,
      gravityY: -40,
    });
  }

  /** Rally Howl — expanding red shockring + upward embers centred on the wolf boss,
   *  played when the White Claw cries to buff nearby pack. */
  playWolfHowl(x: number, y: number): void {
    this.expandingRing(x, y, 0xef4444, 160, 620);
    this.scene.time.delayedCall(120, () => this.expandingRing(x, y, 0x991b1b, 120, 520));
    const flash = this.scene.add.circle(x, y - 30, 26, 0xef4444, 0.7)
      .setDepth(19).setBlendMode(Phaser.BlendModes.ADD);
    this.scene.tweens.add({
      targets: flash, scale: { from: 0.8, to: 2.8 }, alpha: { from: 0.7, to: 0 },
      duration: 620, ease: 'Cubic.easeOut',
      onComplete: () => flash.destroy(),
    });
    this.burstParticles(x, y - 6, 'projectile', 22, {
      speed: { min: 60, max: 200 },
      angle: { min: 0, max: 360 },
      scale: { start: 0.7, end: 0 },
      tint: [0xef4444, 0x991b1b, 0xfbbf24, 0x7f1d1d],
      lifespan: 620,
    });
  }

  /** Brief red "buffed" flash on an ally the White Claw has just rallied. */
  /** Sustained invocation aura — DBZ-style flaming pillar around the hero
   *  for the full buff window. Two pulsing concentric ellipses (additive
   *  blend) + a steady upward particle stream so the silhouette reads as
   *  ki-style rising flame. Optional under-foot ring — Avenging Wrath
   *  drops it per user feedback (ring clipped feet, felt grounded vs.
   *  ablaze); Aegis / Time Pull keep it for the "haloed" look.
   */
  playInvocationAura(
    target: { x: number; y: number; active: boolean },
    durationMs: number,
    colorHex: number,
    opts: { withFootRing?: boolean } = {},
  ): () => void {
    const withFootRing = opts.withFootRing !== false;
    // Inner flame core — bright tight ellipse at the hero's centre of mass.
    const core = this.scene.add.ellipse(target.x, target.y - 28, 28, 56, colorHex, 0.32)
      .setDepth(1.7).setBlendMode(Phaser.BlendModes.ADD);
    // Outer flame halo — wider, dimmer, slower pulse for the silhouette.
    const halo = this.scene.add.ellipse(target.x, target.y - 26, 48, 80, colorHex, 0.18)
      .setDepth(1.6).setBlendMode(Phaser.BlendModes.ADD);
    const foot = withFootRing
      ? this.scene.add.ellipse(target.x, target.y + 4, 50, 14, colorHex, 0.55).setDepth(1.6)
      : null;

    const corePulse = this.scene.tweens.add({
      targets: core,
      scaleX: { from: 0.85, to: 1.20 },
      scaleY: { from: 0.95, to: 1.15 },
      alpha:  { from: 0.40, to: 0.18 },
      duration: 380, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
    });
    const haloPulse = this.scene.tweens.add({
      targets: halo,
      scaleX: { from: 0.90, to: 1.30 },
      scaleY: { from: 1.00, to: 1.20 },
      alpha:  { from: 0.20, to: 0.06 },
      duration: 620, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
    });
    const footPulse = foot
      ? this.scene.tweens.add({
          targets: foot,
          scaleX: { from: 0.85, to: 1.15 },
          alpha:  { from: 0.55, to: 0.25 },
          duration: 500, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
        })
      : null;

    // Follow the hero so the column tracks movement.
    const follow = this.scene.time.addEvent({
      delay: 60, loop: true,
      callback: () => {
        if (!target.active || !core.active) return;
        core.setPosition(target.x, target.y - 28);
        halo.setPosition(target.x, target.y - 26);
        if (foot) foot.setPosition(target.x, target.y + 4);
      },
    });

    // Steady upward ki-style particle stream — fast cadence so the aura
    // reads as continuous flame, not occasional embers.
    const stream = this.scene.time.addEvent({
      delay: 90, loop: true,
      callback: () => {
        if (!target.active || !core.active) return;
        this.burstParticles(target.x, target.y - 4, 'projectile', 2, {
          speed: { min: 60, max: 110 },
          angle: { min: 255, max: 285 }, // straight up with a small spread
          scale: { start: 0.55, end: 0 },
          tint: [colorHex, 0xffffff],
          lifespan: 600,
          gravityY: -180, // strong upward — flames rise hard
        });
      },
    });

    let stopped = false;
    const stop = (): void => {
      if (stopped) return;
      stopped = true;
      corePulse.stop();
      haloPulse.stop();
      footPulse?.stop();
      follow.remove(false);
      stream.remove(false);
      const fadeTargets: Phaser.GameObjects.GameObject[] = foot ? [core, halo, foot] : [core, halo];
      this.scene.tweens.add({
        targets: fadeTargets, alpha: 0, duration: 280,
        onComplete: () => { core.destroy(); halo.destroy(); foot?.destroy(); },
      });
    };
    this.scene.time.delayedCall(durationMs, stop);
    return stop;
  }

  playRallyBuff(x: number, y: number): void {
    const ring = this.scene.add.circle(x, y, 24, 0xef4444, 0)
      .setDepth(19).setStrokeStyle(3, 0xef4444, 0.9);
    this.scene.tweens.add({
      targets: ring, scale: { from: 0.7, to: 1.8 }, alpha: { from: 0.9, to: 0 },
      duration: 420, ease: 'Cubic.easeOut',
      onComplete: () => ring.destroy(),
    });
    this.burstParticles(x, y, 'projectile', 6, {
      speed: { min: 25, max: 80 },
      angle: { min: 220, max: 320 },
      scale: { start: 0.5, end: 0 },
      tint: [0xef4444, 0xfbbf24],
      lifespan: 480,
      gravityY: -30,
    });
  }

  /** Faint ember drift below/around an elite — single short-lived puff intended to be called
   *  on a timer (every ~900ms) while the enemy lives. Cheap; ~3 particles per tick. */
  playEliteAuraTick(x: number, y: number): void {
    this.burstParticles(x, y + 2, 'projectile', 3, {
      speed: { min: 10, max: 35 },
      angle: { min: 230, max: 310 },
      scale: { start: 0.35, end: 0 },
      tint: [0xef4444, 0x991b1b, 0xf97316],
      lifespan: 760,
      gravityY: -30,
    });
  }

  /** Lieutenant ALLY buff — small gold halo + 2-particle puff on a mob
   *  inside the lieutenant's aura. Per user "lieutenant mobs to create
   *  vfx aura to their allies if buff" — players need to see WHO is
   *  being empowered, not just the lieutenant himself. Cheap so it can
   *  fire on every ally per 500 ms aura tick without performance impact. */
  playLieutenantAllyBuffTick(x: number, y: number): void {
    // Tiny gold expanding ring at the buffed mob's feet — short, cheap,
    // reads as "actively empowered right now".
    const ring = this.scene.add.graphics().setDepth(19);
    ring.lineStyle(1.5, 0xffd060, 0.8);
    ring.strokeCircle(0, 0, 22);
    ring.setPosition(x, y).setScale(0.3).setAlpha(0.85);
    this.scene.tweens.add({
      targets: ring, scaleX: 1, scaleY: 1, alpha: 0,
      duration: 420, ease: 'Cubic.easeOut',
      onComplete: () => ring.destroy(),
    });
    // Two warm gold motes drifting upward.
    this.burstParticles(x, y - 4, 'projectile', 2, {
      speed: { min: 8, max: 24 },
      angle: { min: 240, max: 300 },
      scale: { start: 0.32, end: 0 },
      tint: [0xffd060, 0xffe8a0, 0xc8973a],
      lifespan: 540,
      gravityY: -40,
    });
  }

  /** Golden shimmer for loot drops */
  playLootShimmer(x: number, y: number): void {
    this.burstParticles(x, y, 'projectile', 8, {
      speed: { min: 20, max: 55 },
      angle: { min: 250, max: 290 },
      scale: { start: 0.5, end: 0 },
      tint: [0xfbbf24, 0xf59e0b, 0xfef3c7],
      lifespan: 600,
      gravityY: -80,
    });
  }

  /** Sigil pickup "slide into inventory" — spawns the sigil icon at drop location,
   *  tweens it along an arc to the target button, pulses a gold glow ring on arrival.
   *  Self-capping: hard limit on concurrent slides to avoid HUD chaos on bursty drops. */
  private static sigilSlidesInFlight = 0;
  playSigilCollect(fromX: number, fromY: number, toX: number, toY: number, iconKey: string): void {
    if (VFXManager.sigilSlidesInFlight >= 3) {
      // Busy — drop the slide, just play the arrival pulse at the target instantly
      this.expandingRing(toX, toY, 0xf0c040, 40, 260);
      return;
    }
    if (!this.scene.textures.exists(iconKey)) return;
    VFXManager.sigilSlidesInFlight++;
    const icon = this.scene.add.image(fromX, fromY, iconKey)
      .setDisplaySize(32, 32).setDepth(50).setAlpha(0);
    // Fade-in pop, then curved tween toward the Items button, then fade + arrival pulse
    this.scene.tweens.add({
      targets: icon, alpha: 1, duration: 100,
      onComplete: () => {
        const midX = (fromX + toX) / 2;
        const midY = Math.min(fromY, toY) - 60; // arc up over the battlefield
        const duration = 520;
        const ctrl = { t: 0 };
        this.scene.tweens.add({
          targets: ctrl, t: 1, duration, ease: 'Cubic.easeIn',
          onUpdate: () => {
            // Quadratic Bezier P(t) = (1-t)² P0 + 2(1-t)t P1 + t² P2
            const u = 1 - ctrl.t;
            icon.x = u * u * fromX + 2 * u * ctrl.t * midX + ctrl.t * ctrl.t * toX;
            icon.y = u * u * fromY + 2 * u * ctrl.t * midY + ctrl.t * ctrl.t * toY;
            icon.setScale(1 - 0.4 * ctrl.t); // shrink slightly as it slides in
          },
          onComplete: () => {
            icon.destroy();
            VFXManager.sigilSlidesInFlight--;
            // Arrival: gold ring + small burst at the button
            this.expandingRing(toX, toY, 0xf0c040, 50, 300);
            this.burstParticles(toX, toY, 'projectile', 6, {
              speed: { min: 40, max: 80 },
              angle: { min: 220, max: 320 },
              scale: { start: 0.5, end: 0 },
              tint: [0xf0c040, 0xfef3c7, 0xc8973a],
              lifespan: 320,
            });
          },
        });
      },
    });
  }

  /** Brief gold pulse + sparkle at the rune-counter position. Used when loot increases.
   *  Caller is responsible for debouncing — this just renders the effect. */
  /** Book-pick flash on a tome card — scale-up + gold-tinted stroke flash + a
   *  sparkle burst from the card centre. Called right when the player clicks a
   *  tome card so the pick reads as a selection beat instead of an instant
   *  popup close. Tint is the tome's signature colour. */
  playTomePickFlash(card: Phaser.GameObjects.Rectangle | null, cx: number, cy: number, tint: number): void {
    // Card-pick confirmation effect. The `card` arg is optional so callers
    // can pass null when their card is a Container/Graphics combo that
    // owns its own scale tween (the new tome popup uses cardContainer).
    if (card) {
      const startScale = card.scaleX || 1;
      this.scene.tweens.add({
        targets: card, scaleX: startScale * 1.12, scaleY: startScale * 1.12,
        duration: 150, yoyo: true, ease: 'Sine.easeOut',
        onComplete: () => card.setScale(startScale),
      });
      const originalStrokeColor = (card as unknown as { strokeColor: number }).strokeColor;
      card.setStrokeStyle(3, 0xffe8a0, 1);
      this.scene.time.delayedCall(260, () => {
        if (!card.active) return;
        card.setStrokeStyle(2, originalStrokeColor, 1);
      });
    }
    // Sparkle burst from card centre.
    this.burstParticles(cx, cy, 'projectile', 8, {
      speed: { min: 40, max: 120 },
      angle: { min: 0, max: 360 },
      scale: { start: 0.55, end: 0 },
      tint: [tint, 0xffe8a0, 0xfef3c7],
      lifespan: 420,
    });
    // Expanding gold ring at the card centre for extra "pick confirmed" flair.
    this.expandingRing(cx, cy, 0xffd060, 60, 320);
  }

  /** Tome HUD-icon pulse — brief scale-up on the bg + icon + count, gold tint
   *  flash, and a small sparkle behind. Mirrors the existing rune-counter
   *  flash so a newly-picked book reads as "this one just levelled" at the
   *  same visual language as the loot counter. */
  playTomeIconPulse(targets: Phaser.GameObjects.GameObject[], cx: number, cy: number, tint: number): void {
    for (const t of targets) {
      if (!t.active) continue;
      // Transform-like game objects (Rectangle / Text) expose scaleX/scaleY +
      // setScale — cast through unknown because the base GameObject type
      // doesn't include the Transform mixin.
      const scalable = t as unknown as { scaleX: number; setScale: (s: number) => void };
      const startScale = scalable.scaleX || 1;
      this.scene.tweens.add({
        targets: t, scaleX: startScale * 1.40, scaleY: startScale * 1.40,
        duration: 180, yoyo: true, ease: 'Sine.easeOut',
        onComplete: () => scalable.setScale(startScale),
      });
    }
    // Sparkle burst behind the icon.
    this.burstParticles(cx, cy, 'projectile', 8, {
      speed: { min: 30, max: 70 },
      angle: { min: 0, max: 360 },
      scale: { start: 0.5, end: 0 },
      tint: [tint, 0xfef3c7],
      lifespan: 420,
    });
    this.expandingRing(cx, cy, tint, 44, 320);
  }

  playRuneFlash(textObj: Phaser.GameObjects.Text): void {
    const startScale = textObj.scaleX || 1;
    const origColor = textObj.style.color;
    textObj.setColor('#ffe8a0');
    this.scene.tweens.add({
      targets: textObj, scaleX: startScale * 1.18, scaleY: startScale * 1.18,
      duration: 140, yoyo: true, ease: 'Sine.easeOut',
      onComplete: () => {
        textObj.setScale(startScale);
        textObj.setColor(origColor);
      },
    });
    // Small sparkle burst behind the text
    const burstX = textObj.x + 24;
    const burstY = textObj.y;
    this.burstParticles(burstX, burstY, 'projectile', 5, {
      speed: { min: 20, max: 50 },
      angle: { min: 0, max: 360 },
      scale: { start: 0.45, end: 0 },
      tint: [0xf0c040, 0xfef3c7],
      lifespan: 360,
    });
  }

  /** Stun/Earthquake — camera shake + orange ring burst */
  playStunQuake(x: number, y: number): void {
    this.scene.cameras.main.shake(20, 0.0004);
    this.expandingRing(x, y, 0xf97316, 70, 400);
    this.burstParticles(x, y, 'projectile', 16, {
      speed: { min: 40, max: 120 },
      angle: { min: 0, max: 360 },
      scale: { start: 0.6, end: 0 },
      tint: [0xf97316, 0xfbbf24, 0xfef3c7],
      lifespan: 380,
    });
  }

  /** Ground spike burst at target feet — Warrior Earthquake (sprite at feet, origin bottom) */
  playSpikes(x: number, y: number): void {
    this.playFxSprite(x, y, 'effect_spikes', 'fx_spikes', 72, 1.0);
  }

  /** Telurion AoE — slate tornado (magic-spell-pack 9, 6 frames). Plays once
   *  at the impact center for spells that hit a ground area: Verdant Spike
   *  Field eruptions and Quake Roar shockwave. Scales the 72 px frame up so
   *  the visual matches the actual hit radius. Falls back silently on the
   *  legacy spike burst when the dedicated texture/anim isn't loaded. */
  playTelurionAoe(x: number, y: number, size = 180): void {
    if (!this.scene.textures.exists('effect_telurion_aoe')
        || !this.scene.anims.exists('fx_telurion_aoe')) {
      this.playSpikes(x, y);
      return;
    }
    const burst = this.scene.add.sprite(x, y, 'effect_telurion_aoe')
      .setDisplaySize(size, size)
      .setDepth(8)
      .setOrigin(0.5, 0.5)
      .setBlendMode(Phaser.BlendModes.ADD);
    burst.play('fx_telurion_aoe');
    burst.once('animationcomplete', () => burst.destroy());
  }

  /** Telurion single-target — cyan ring/burst (magic-spell-pack 8, 8 frames).
   *  Used for Stormcall's per-hero strike (and any future single-target
   *  Telurion spell). Smaller display size than the AoE so a multi-target
   *  Stormcall doesn't drown the screen in overlapping rings. Falls back on
   *  the legacy lightning-bolt VFX when the dedicated texture isn't loaded. */
  playTelurionStrike(x: number, y: number, size = 130): void {
    if (!this.scene.textures.exists('effect_telurion_st')
        || !this.scene.anims.exists('fx_telurion_st')) {
      this.playLightningBolt(x, y);
      return;
    }
    const strike = this.scene.add.sprite(x, y - 24, 'effect_telurion_st')
      .setDisplaySize(size, size)
      .setDepth(18)
      .setOrigin(0.5, 0.5)
      .setBlendMode(Phaser.BlendModes.ADD);
    strike.play('fx_telurion_st');
    strike.once('animationcomplete', () => strike.destroy());
  }

  /** Telurion travelling strike — same VFX as playTelurionStrike but the
   *  cyan/violet orb starts at (fromX, fromY) and arcs to (toX, toY) before
   *  detonating. Reads as "Telurion HURLS the spell at the target" instead
   *  of "spell appears on top of target". A short trail of fading orbs is
   *  spawned along the path so the missile reads even at high speed.
   *
   *  Returns the travel duration in ms so the caller can chain damage to
   *  the moment of impact (rather than the moment of cast). */
  playTelurionStrikeTravel(fromX: number, fromY: number, toX: number, toY: number, size = 130): number {
    if (!this.scene.textures.exists('effect_telurion_st')
        || !this.scene.anims.exists('fx_telurion_st')) {
      this.playLightningBolt(toX, toY);
      return 0;
    }
    const dx = toX - fromX;
    const dy = (toY - 24) - (fromY - 20);
    const dist = Math.hypot(dx, dy);
    // ~0.85 px / ms — fast enough to feel decisive on a 960 px-wide canvas
    // (a full-canvas shot lands in just over a second), slow enough that
    // the player tracks the orb visually rather than seeing a teleport.
    const duration = Math.max(220, Math.min(900, Math.round(dist / 0.85)));
    // Travelling orb — smaller than the impact burst (the impact carries
    // the visual weight; the travel is the connective tissue).
    const orbSize = Math.round(size * 0.55);
    const orb = this.scene.add.sprite(fromX, fromY - 20, 'effect_telurion_st')
      .setDisplaySize(orbSize, orbSize)
      .setDepth(17)
      .setOrigin(0.5, 0.5)
      .setBlendMode(Phaser.BlendModes.ADD)
      .setRotation(Math.atan2(dy, dx));
    orb.play('fx_telurion_st');
    // Subtle trail — a lingering ghost of the orb every ~50 ms along the path
    // so the missile reads as "screaming through the air" rather than a
    // silent slide. Each ghost fades + shrinks then self-destructs.
    const trailEvent = this.scene.time.addEvent({
      delay: 50, loop: true,
      callback: () => {
        if (!orb.active) return;
        const ghost = this.scene.add.image(orb.x, orb.y, 'effect_telurion_st', 3)
          .setDisplaySize(orbSize * 0.7, orbSize * 0.7)
          .setDepth(16)
          .setOrigin(0.5, 0.5)
          .setBlendMode(Phaser.BlendModes.ADD)
          .setAlpha(0.55);
        this.scene.tweens.add({
          targets: ghost, alpha: 0, scale: ghost.scale * 0.55, duration: 240,
          onComplete: () => ghost.destroy(),
        });
      },
    });
    this.scene.tweens.add({
      targets: orb,
      x: toX,
      y: toY - 24,
      duration,
      ease: 'Sine.easeIn',
      onComplete: () => {
        trailEvent.remove();
        orb.destroy();
        // Impact pop — the original full-size burst lands at the target.
        this.playTelurionStrike(toX, toY, size);
      },
    });
    return duration;
  }

  /** @deprecated Use playTelurionAoe — kept as a thin alias for any caller
   *  not yet updated to the new naming. */
  playVerdantBurst(x: number, y: number): void {
    this.playTelurionAoe(x, y);
  }

  /** @deprecated Use playTelurionStrike — kept as a thin alias. */
  playStormKite(x: number, y: number): void {
    this.playTelurionStrike(x, y);
  }

  /** Malakhar Void Hex — stone-shard eruption with violet wash. Played on
   *  trap detonation; BattleScene then keeps the hex slot permanently
   *  disabled for the run via its own grid-render path. Falls back to a
   *  legacy ring/burst if the dedicated sheet didn't ship.
   *
   *  Tinted violet (0xa040ff) to fit Malakhar's palette + a slight ADD
   *  blend so the spikes glow against the dim arena background. */
  playVoidHex(x: number, y: number, size = 150): void {
    if (!this.scene.textures.exists('effect_void_hex')
        || !this.scene.anims.exists('fx_void_hex')) {
      this.expandingRing(x, y, 0xa040ff, 60, 700);
      this.playSpikes(x, y);
      return;
    }
    const sprite = this.scene.add.sprite(x, y, 'effect_void_hex')
      .setDisplaySize(size, size)
      .setDepth(8)
      .setOrigin(0.5, 0.5)
      .setBlendMode(Phaser.BlendModes.ADD)
      .setTint(0xa040ff);
    sprite.play('fx_void_hex');
    sprite.once('animationcomplete', () => sprite.destroy());
  }

  /** Malakhar phase-3 Kill-Zone telegraph — uses the dedicated
   *  `effect_shadowlord_killzone` strip (ree-pixel-magic 2.png) that was
   *  shipped but unused. Renders the lethal-band warning as a violet
   *  conjure ring + sigil flare instead of a placeholder add.circle.
   *  Caller still draws the inner/outer GAMEPLAY rings (they communicate
   *  the safe zones) — this VFX layers on top for the menacing read. */
  playShadowlordKillzoneTelegraph(x: number, y: number, size = 380): void {
    if (!this.scene.textures.exists('effect_shadowlord_killzone')
        || !this.scene.anims.exists('fx_shadowlord_killzone')) {
      this.expandingRing(x, y, 0xff2080, 200, 900);
      return;
    }
    const sigil = this.scene.add.sprite(x, y, 'effect_shadowlord_killzone')
      .setDisplaySize(size, size)
      .setDepth(7)
      .setOrigin(0.5, 0.5)
      .setBlendMode(Phaser.BlendModes.ADD)
      .setAlpha(0.85);
    sigil.play('fx_shadowlord_killzone');
    sigil.once('animationcomplete', () => sigil.destroy());
  }

  /** Malakhar's traveling shadow orb — sprite-based version using the
   *  `effect_shadowlord_impact` strip (ree-pixel-magic 10.png) for both
   *  the projectile body AND the impact pop, so the spell reads with a
   *  consistent visual identity across cast → travel → land. The strip
   *  loops while the orb is in flight and replays at 2× scale on arrival
   *  for the detonation. Replaces the earlier procedural circle/halo/
   *  core orb (user feedback: "the vfx use for auto attack is bad").
   *
   *  Returns the travel duration in ms so the caller can chain damage to
   *  the moment of impact rather than the moment of cast. */
  playShadowlordOrb(fromX: number, fromY: number, toX: number, toY: number): number {
    if (!this.scene.textures.exists('effect_shadowlord_impact')
        || !this.scene.anims.exists('fx_shadowlord_impact')) {
      // Fallback: violet expanding ring at target so the spell still reads
      // even if the asset failed to load.
      this.expandingRing(toX, toY, 0xa040ff, 50, 360);
      return 320;
    }
    const dx = toX - fromX;
    const dy = toY - fromY;
    const dist = Math.hypot(dx, dy);
    // 0.65 px/ms — heavy/pulsing read; player gets a beat to anticipate.
    const duration = Math.max(300, Math.min(1100, Math.round(dist / 0.65)));
    // Looping anim variant for the projectile body — auto-loop spec was
    // a single play (repeat: 0); we want it to cycle while the orb
    // travels so the visual stays alive. Lazy-create on first call.
    const LOOP_KEY = 'fx_shadowlord_impact_loop';
    if (!this.scene.anims.exists(LOOP_KEY)) {
      this.scene.anims.create({
        key: LOOP_KEY,
        frames: this.scene.anims.generateFrameNumbers('effect_shadowlord_impact', { start: 0, end: 5 }),
        frameRate: 14,
        repeat: -1,
      });
    }
    // Projectile sprite — small (96 px) while traveling, ADD-blended for
    // the cyan-violet glow read. Rotation aligned to the travel direction
    // so the sprite faces forward like a missile.
    const orb = this.scene.add.sprite(fromX, fromY, 'effect_shadowlord_impact')
      .setDisplaySize(96, 96)
      .setDepth(18)
      .setOrigin(0.5, 0.5)
      .setBlendMode(Phaser.BlendModes.ADD)
      .setRotation(Math.atan2(dy, dx))
      .setTint(0xb060ff);
    orb.play(LOOP_KEY);
    // Trailing ghost copies every ~50 ms so the missile reads "screaming
    // through the air" rather than a silent slide. Each ghost fades + shrinks.
    const trailEvent = this.scene.time.addEvent({
      delay: 55, loop: true,
      callback: () => {
        if (!orb.active) return;
        const ghost = this.scene.add.image(orb.x, orb.y, 'effect_shadowlord_impact', 2)
          .setDisplaySize(72, 72)
          .setDepth(17)
          .setOrigin(0.5, 0.5)
          .setBlendMode(Phaser.BlendModes.ADD)
          .setAlpha(0.55)
          .setTint(0x6020a0);
        this.scene.tweens.add({
          targets: ghost, alpha: 0, scale: ghost.scale * 0.55, duration: 260,
          onComplete: () => ghost.destroy(),
        });
      },
    });
    // Two-stage arc — Phaser tween simulation since simple shape tweens
    // don't follow path objects. Rise to midpoint then fall to target.
    const arcMidX = (fromX + toX) / 2;
    const arcMidY = Math.min(fromY, toY) - 60;
    this.scene.tweens.add({
      targets: orb, x: arcMidX, y: arcMidY,
      duration: duration / 2, ease: 'Sine.easeOut',
    });
    this.scene.tweens.add({
      targets: orb, x: toX, y: toY,
      delay: duration / 2,
      duration: duration / 2, ease: 'Sine.easeIn',
      onComplete: () => {
        trailEvent.remove();
        orb.destroy();
        // Detonation — replay the impact anim at 2× scale, full bright
        // (no tint) so the landing pops harder than the projectile body.
        const burst = this.scene.add.sprite(toX, toY, 'effect_shadowlord_impact')
          .setDisplaySize(192, 192)
          .setDepth(20)
          .setOrigin(0.5, 0.5)
          .setBlendMode(Phaser.BlendModes.ADD);
        burst.play('fx_shadowlord_impact');
        burst.once('animationcomplete', () => burst.destroy());
      },
    });
    return duration;
  }

  /** Endless-mode ghost rift — pale cyan-white expansion when a ghost
   *  fades into existence at a screen edge. Quieter and colder than the
   *  Shadow Veil summon portal (violet/black) so the player reads the
   *  two as different threat tiers: bosses through rifts, ghosts as
   *  thin spectral seepage from the broken veil. */
  playGhostRift(x: number, y: number): void {
    this.expandingRing(x, y, 0xe0f4ff, 36, 380);
    this.scene.time.delayedCall(60, () => this.expandingRing(x, y, 0x90c8e0, 60, 460));
    this.burstParticles(x, y, 'projectile', 6, {
      speed: { min: 25, max: 70 },
      angle: { min: 200, max: 340 },
      scale: { start: 0.32, end: 0 },
      tint: [0xe0f4ff, 0x90c8e0, 0xa0d8f0, 0xffffff],
      lifespan: 480,
    });
  }

  /** Shadow Veil summon portal — violet/black double-ring expansion plus a
   *  rising swarm of dark wisps. Used for Phase 2 boss-summon spawn points
   *  on Malakhar. Distinct from the kill-zone impact (single sprite burst)
   *  and the generic death poof so the player reads "rift opening" instead
   *  of "another hit landed". */
  playSummonPortal(x: number, y: number): void {
    this.expandingRing(x, y, 0xc080ff, 50, 480);
    this.scene.time.delayedCall(80,  () => this.expandingRing(x, y, 0x9d4dff, 80,  600));
    this.scene.time.delayedCall(180, () => this.expandingRing(x, y, 0x4a0066, 120, 760));
    // Rising void wisps — narrow upward fan so the read is "things being
    // pulled UP through a rift" rather than a generic explosion.
    this.burstParticles(x, y, 'projectile', 14, {
      speed: { min: 35, max: 110 },
      angle: { min: 215, max: 325 },
      scale: { start: 0.5, end: 0 },
      tint: [0x9d4dff, 0x4a0066, 0x180022, 0x6020a0, 0xc080ff],
      lifespan: 720,
      rotate: { min: 0, max: 360 },
    });
    // Soft black flash at center to anchor the rift origin.
    const flash = this.scene.add.circle(x, y, 14, 0x180022, 0.85)
      .setDepth(19).setBlendMode(Phaser.BlendModes.MULTIPLY);
    this.scene.tweens.add({ targets: flash, alpha: 0, scale: 3.2, duration: 600, onComplete: () => flash.destroy() });
  }

  /** Malakhar Phase-3 kill-zone DETONATION — procedural cataclysm: a
   *  triple-shockwave + skull/bone wisps radiating outward in the lethal
   *  band. Used to be `playShadowlordImpact` (ree-pixel-magic 10) but
   *  that asset is now exclusive to his auto-attack orb (no-VFX-reuse
   *  rule), so this is a procedural ring-storm instead. */
  playShadowlordRift(x: number, y: number): void {
    // Triple expanding shockwave — staggered + alternating palette so the
    // detonation reads as multi-stage instead of one ring.
    this.expandingRing(x, y, 0xff2080, 80,  500);
    this.scene.time.delayedCall(80,  () => this.expandingRing(x, y, 0xa040ff, 160, 700));
    this.scene.time.delayedCall(180, () => this.expandingRing(x, y, 0x4a0066, 240, 900));
    // Outward bone-wisp burst — radial spray into the lethal band.
    this.burstParticles(x, y, 'projectile', 22, {
      speed: { min: 90, max: 220 },
      angle: { min: 0, max: 360 },
      scale: { start: 0.55, end: 0 },
      tint: [0xff2080, 0xa040ff, 0xffffff, 0x4a0066],
      lifespan: 600,
      rotate: { min: 0, max: 360 },
    });
    // Center white flash — anchors the detonation point.
    const flash = this.scene.add.circle(x, y, 28, 0xffffff, 0.95)
      .setDepth(20).setBlendMode(Phaser.BlendModes.ADD);
    this.scene.tweens.add({
      targets: flash, alpha: 0, scale: 3.6, duration: 320,
      onComplete: () => flash.destroy(),
    });
  }

  /** @deprecated Use playShadowlordRift for kill-zone impact and the
   *  bespoke orb-detonation logic inside playShadowlordOrb for the
   *  auto-attack landing. Left as a thin alias so legacy call sites
   *  still resolve, but new code should not call this. */
  playShadowlordImpact(x: number, y: number, size = 320): void {
    if (!this.scene.textures.exists('effect_shadowlord_impact')
        || !this.scene.anims.exists('fx_shadowlord_impact')) {
      this.playSpikes(x, y);
      return;
    }
    const burst = this.scene.add.sprite(x, y, 'effect_shadowlord_impact')
      .setDisplaySize(size, size)
      .setDepth(8)
      .setOrigin(0.5, 0.5)
      .setBlendMode(Phaser.BlendModes.ADD);
    burst.play('fx_shadowlord_impact');
    burst.once('animationcomplete', () => burst.destroy());
  }

  /** Whirlwind — red crescent slash sprite animation scaled big to cover the area. */
  playWhirlwind(x: number, y: number): void {
    const hasSheet = this.scene.textures.exists('effect_combat_fx') && this.scene.anims.exists('fx_whirlwind_slash');
    if (hasSheet) {
      // Two overlapping slashes rotated opposite for a spin effect
      const a = this.scene.add.sprite(x, y, 'effect_combat_fx', 240)
        .setDisplaySize(160, 160).setDepth(20).setBlendMode(Phaser.BlendModes.ADD).setRotation(-0.3);
      const b = this.scene.add.sprite(x, y, 'effect_combat_fx', 240)
        .setDisplaySize(140, 140).setDepth(20).setBlendMode(Phaser.BlendModes.ADD)
        .setRotation(Math.PI + 0.3).setFlipX(true).setAlpha(0.85);
      a.play('fx_whirlwind_slash');
      b.play('fx_whirlwind_slash');
      a.once('animationcomplete', () => a.destroy());
      b.once('animationcomplete', () => b.destroy());
      this.scene.tweens.add({ targets: a, rotation: a.rotation + Math.PI * 0.7, duration: 500, ease: 'Cubic.easeOut' });
      this.scene.tweens.add({ targets: b, rotation: b.rotation - Math.PI * 0.7, duration: 500, ease: 'Cubic.easeOut' });
    }
    // Supporting dust and ring so it still reads well even if sheet fails to load
    this.expandingRing(x, y, 0xffa060, 56, 320);
    this.burstParticles(x, y, 'projectile', 10, {
      speed: { min: 35, max: 95 },
      angle: { min: 0, max: 360 },
      scale: { start: 0.22, end: 0 },
      tint: [0xffa060, 0xffd08c, 0xff8040, 0xffffff],
      lifespan: 420,
      rotate: { min: 0, max: 360 },
    });
  }

  /** Sylvari Multishot fan — green-tinted arrows with a nature-theme release burst + leaf particles. */
  playMultishotFan(fromX: number, fromY: number, targets: Array<{ x: number; y: number }>): void {
    // Nature-green release pulse — triple ring for energy buildup
    this.expandingRing(fromX, fromY, 0x86efac, 26, 260);
    this.scene.time.delayedCall(60, () => this.expandingRing(fromX, fromY, 0x4ade80, 34, 240));
    // Leaf-like release particles — a fanning spray of small green diamonds
    this.burstParticles(fromX, fromY, 'projectile', 10, {
      speed: { min: 40, max: 110 },
      angle: { min: -55, max: 55 },
      scale: { start: 0.35, end: 0 },
      tint: [0x86efac, 0x4ade80, 0xbbf7d0, 0xf0fdf4],
      lifespan: 320,
    });
    // Small white flash right on the bowstring
    const flash = this.scene.add.circle(fromX, fromY, 6, 0xffffff, 0.9)
      .setDepth(20).setBlendMode(Phaser.BlendModes.ADD);
    this.scene.tweens.add({ targets: flash, alpha: 0, scale: 2.4, duration: 200, onComplete: () => flash.destroy() });
    // Fire arrows in rapid sequence (30ms apart) — each one leaves a green trail via playArrow + impact leaves
    for (let i = 0; i < targets.length; i++) {
      const t = targets[i];
      this.scene.time.delayedCall(i * 30, () => {
        this.playArrow(fromX, fromY, t.x, t.y);
        // Per-target impact leaf burst
        this.scene.time.delayedCall(160, () => {
          this.burstParticles(t.x, t.y, 'projectile', 6, {
            speed: { min: 30, max: 85 },
            angle: { min: 0, max: 360 },
            scale: { start: 0.3, end: 0 },
            tint: [0x4ade80, 0x86efac, 0xbbf7d0],
            lifespan: 350,
          });
        });
      });
    }
  }

  /** Spinning stun stars above a target's head — circles for `durationMs`. */
  /** Orbiting stars above the target's head for the stun duration. Pins to the target's actual
   *  sprite height so the stars sit on the head on minions AND on bosses. Accepts an optional
   *  `displayHeight` so callers with variable sprite sizes position correctly. */
  playStunStars(
    target: { x: number; y: number; active: boolean; displayHeight?: number },
    durationMs: number,
  ): void {
    const star1 = this.scene.add.text(0, 0, '✦', {
      fontSize: '14px', fontFamily: 'Palatino Linotype, Georgia, serif',
      color: '#fde68a', stroke: '#0a0402', strokeThickness: 3,
    }).setResolution(2).setOrigin(0.5).setDepth(28);
    const star2 = this.scene.add.text(0, 0, '✦', {
      fontSize: '14px', fontFamily: 'Palatino Linotype, Georgia, serif',
      color: '#fef3c7', stroke: '#0a0402', strokeThickness: 3,
    }).setResolution(2).setOrigin(0.5).setDepth(28);
    const star3 = this.scene.add.text(0, 0, '✧', {
      fontSize: '12px', fontFamily: 'Palatino Linotype, Georgia, serif',
      color: '#fbbf24', stroke: '#0a0402', strokeThickness: 3,
    }).setResolution(2).setOrigin(0.5).setDepth(28);
    const stars = [star1, star2, star3];
    const startTime = this.scene.time.now;
    const ORBIT_R = 16;
    const upd = this.scene.time.addEvent({
      delay: 16, loop: true,
      callback: () => {
        if (!target.active) { upd.remove(); stars.forEach(s => s.destroy()); return; }
        const t = (this.scene.time.now - startTime) / 1000;
        // Enemy origin is (0.5, 1.0) so target.y is the feet. Put the stars just above the sprite top.
        const h = target.displayHeight ?? 68;
        const headY = target.y - h - 8;
        for (let i = 0; i < stars.length; i++) {
          const a = t * 6 + (i * (Math.PI * 2) / stars.length);
          stars[i].x = target.x + Math.cos(a) * ORBIT_R;
          stars[i].y = headY + Math.sin(a) * 4;
          stars[i].setRotation(a);
        }
      },
    });
    this.scene.time.delayedCall(durationMs, () => {
      upd.remove();
      this.scene.tweens.add({
        targets: stars, alpha: 0, duration: 200,
        onComplete: () => stars.forEach(s => s.destroy()),
      });
    });
  }

  /** Tiny violet spark on impact — Rogue Sinister Strike */
  playSinisterStrike(x: number, y: number): void {
    this.burstParticles(x, y, 'projectile', 6, {
      speed: { min: 20, max: 55 },
      angle: { min: 0, max: 360 },
      scale: { start: 0.25, end: 0 },
      tint: [0x1a4878, 0x3070a0, 0x80c0e0],
      lifespan: 180,
    });
  }

  /** Lightning strike on impact — Rogue Shuriken Barrage */
  playLightning(x: number, y: number): void {
    this.playFxSprite(x, y, 'effect_lightning', 'fx_lightning', 56, 1.0);
  }

  /** Lightning bolt on impact — Archer Piercing Shot */
  playLightningBolt(x: number, y: number): void {
    this.playFxSprite(x, y, 'effect_lightning_bolt', 'fx_lightning_bolt', 56, 0.5);
  }

  /** Zhorak auto-attack bolt — a zig-zag lightning line from hero to target plus
   *  a strike-on-impact flash. Distinct from the totem's `playLightning` zap so
   *  the shaman's direct auto-attack reads as "hero casts" and the totem ticks
   *  read as "totem channels". */
  playShamanBolt(fromX: number, fromY: number, toX: number, toY: number): void {
    // Zig-zag line via Graphics (cheap, 1 frame object, auto-destroys on tween complete).
    const g = this.scene.add.graphics().setDepth(22);
    g.lineStyle(2, 0xbde3ff, 0.95);
    const segs = 5;
    let px = fromX, py = fromY;
    g.beginPath();
    g.moveTo(px, py);
    for (let i = 1; i <= segs; i++) {
      const t = i / segs;
      const cx = fromX + (toX - fromX) * t;
      const cy = fromY + (toY - fromY) * t;
      // Jitter perpendicular to the line for the zig-zag look; last segment lands clean.
      const dx = toX - fromX, dy = toY - fromY;
      const len = Math.max(1, Math.hypot(dx, dy));
      const nx = -dy / len, ny = dx / len;
      const jitter = i === segs ? 0 : (Math.random() - 0.5) * 18;
      px = cx + nx * jitter;
      py = cy + ny * jitter;
      g.lineTo(px, py);
    }
    g.strokePath();
    // Outer glow — thicker translucent stroke on top for a soft halo.
    g.lineStyle(5, 0x38bdf8, 0.35);
    g.strokePath();
    this.scene.tweens.add({ targets: g, alpha: 0, duration: 220, ease: 'Cubic.easeOut', onComplete: () => g.destroy() });
    // Impact flash at target.
    if (this.scene.textures.exists('effect_lightning_bolt_strike')) {
      this.playFxSprite(toX, toY, 'effect_lightning_bolt_strike', 'fx_lightning_bolt_strike', 56, 0.4);
    } else {
      this.expandingRing(toX, toY, 0x38bdf8, 36, 260);
    }
  }

  /** Shaman auto-attack — spirit wisp orb flying to target with a small splash on impact.
   *  Visually distinct from Lootfi's lightning/boomerang auto and from Kael's boomerang. */
  playSpiritWisp(fromX: number, fromY: number, toX: number, toY: number): void {
    const dist = Math.hypot(toX - fromX, toY - fromY);
    const duration = Math.max(160, dist * 0.45);
    const orb = this.scene.add.graphics().setDepth(21);
    orb.fillStyle(0xd8b4fe, 0.9);
    orb.fillCircle(0, 0, 5);
    orb.fillStyle(0xfbcfe8, 0.6);
    orb.fillCircle(0, 0, 8);
    orb.setPosition(fromX, fromY);
    this.scene.tweens.add({
      targets: orb, x: toX, y: toY, duration, ease: 'Sine.easeInOut',
      onComplete: () => {
        this.expandingRing(toX, toY, 0xd8b4fe, 32, 260);
        if (this.scene.textures.exists('spark')) {
          this.burstParticles(toX, toY, 'spark', 6, {
            speed: { min: 40, max: 110 }, lifespan: 260, scale: { start: 0.55, end: 0 },
            tint: [0xd8b4fe, 0xfbcfe8, 0xf5d0fe], alpha: { start: 1, end: 0 },
          });
        }
        orb.destroy();
      },
    });
  }

  /** Lightning totem zap — top-down crack of lightning onto the victim. Different from
   *  the existing playLightning sprite so it doesn't collide with Kael's boomerang VFX. */
  playTotemStrike(x: number, y: number): void {
    // Vertical jagged bolt coming from off-screen top → target
    const g = this.scene.add.graphics().setDepth(22);
    g.lineStyle(3, 0xfef08a, 0.95);
    const jitterX = 12;
    const topY = Math.max(0, y - 220);
    let py = topY;
    let px = x + (Math.random() - 0.5) * jitterX;
    g.beginPath();
    g.moveTo(px, py);
    const segs = 6;
    for (let i = 1; i <= segs; i++) {
      const t = i / segs;
      py = topY + (y - topY) * t;
      px = x + (i === segs ? 0 : (Math.random() - 0.5) * jitterX * 2);
      g.lineTo(px, py);
    }
    g.strokePath();
    g.lineStyle(7, 0x60a5fa, 0.35);
    g.strokePath();
    this.scene.tweens.add({
      targets: g, alpha: 0, duration: 260, ease: 'Cubic.easeOut', onComplete: () => g.destroy(),
    });
    this.expandingRing(x, y, 0xfef08a, 34, 260);
  }

  /** Red shamanic party buff — flame-wreath burst on each empowered ally. */
  playShamanicBuff(x: number, y: number): void {
    this.expandingRing(x, y - 30, 0xdc2626, 48, 380);
    this.scene.time.delayedCall(60, () => this.expandingRing(x, y - 30, 0xf97316, 36, 320));
    if (this.scene.textures.exists('spark')) {
      this.burstParticles(x, y - 16, 'spark', 10, {
        speedY: { min: -100, max: -40 }, speedX: { min: -30, max: 30 }, lifespan: 520,
        scale: { start: 0.7, end: 0 }, tint: [0xdc2626, 0xf97316, 0xfbbf24],
        alpha: { start: 1, end: 0 },
      });
    }
  }

  /** Spirit Shield cast flash — blue for mana, gold for lightning. */
  playSpiritShield(x: number, y: number, kind: 'mana' | 'lightning'): void {
    const primary   = kind === 'mana' ? 0x60a5fa : 0xfde68a;
    const secondary = kind === 'mana' ? 0xbfdbfe : 0xfbbf24;
    this.expandingRing(x, y - 40, primary,   50, 360);
    this.scene.time.delayedCall(80, () => this.expandingRing(x, y - 40, secondary, 36, 300));
  }

  /** Lightning Shield reflect — quick bolt arc from hero back to attacker. */
  playShieldReflect(fromX: number, fromY: number, toX: number, toY: number): void {
    const g = this.scene.add.graphics().setDepth(22);
    g.lineStyle(2, 0xfde68a, 0.9);
    g.beginPath(); g.moveTo(fromX, fromY);
    const mx = (fromX + toX) / 2 + (Math.random() - 0.5) * 12;
    const my = (fromY + toY) / 2 + (Math.random() - 0.5) * 12;
    g.lineTo(mx, my); g.lineTo(toX, toY);
    g.strokePath();
    this.scene.tweens.add({ targets: g, alpha: 0, duration: 240, onComplete: () => g.destroy() });
  }

  /** Mana Shield drink — brief blue flare on Zhorak when a stack restores mana. */
  playManaReturn(x: number, y: number): void {
    this.expandingRing(x, y - 30, 0x60a5fa, 28, 240);
  }

  /** Healing-totem pulse on the healed ally — persistent green aura column around
   *  the target + expanding ring + upward motes. Reads loudly when the totem ticks
   *  a heal even if the base playHealGlow asset is missing. */
  playHealingTotemPulse(x: number, y: number): void {
    // Ground ring — bright green, wider.
    this.expandingRing(x, y - 8, 0x10b981, 60, 500);
    this.scene.time.delayedCall(70, () => this.expandingRing(x, y - 8, 0x6ee7b7, 40, 420));
    // Aura column — a tall semi-transparent green rectangle that fades up around the ally.
    const aura = this.scene.add.rectangle(x, y - 40, 46, 80, 0x10b981, 0.35).setDepth(20);
    this.scene.tweens.add({
      targets: aura,
      y: y - 70,
      alpha: 0,
      scaleX: 1.2,
      scaleY: 1.15,
      duration: 680,
      ease: 'Cubic.easeOut',
      onComplete: () => aura.destroy(),
    });
    // Rising motes — give depth and keep readability even on busy backgrounds.
    if (this.scene.textures.exists('spark')) {
      this.burstParticles(x, y - 10, 'spark', 10, {
        speedY: { min: -120, max: -60 }, speedX: { min: -30, max: 30 }, lifespan: 700,
        scale: { start: 0.7, end: 0 }, tint: [0x10b981, 0xa7f3d0, 0xecfdf5],
        alpha: { start: 1, end: 0 },
      });
    }
  }

  /** Earthbind slow — leaning (perspective-squashed) ring at the enemy's feet.
   *  Scales vertically less than horizontally so it reads as a ground-plane wave
   *  rather than a flat circle; feels like a hovering dimensional aura. */
  playEarthbindSlow(x: number, y: number): void {
    const makeWave = (color: number, r: number, dur: number) => {
      const g = this.scene.add.graphics().setDepth(19);
      g.lineStyle(2, color, 0.9);
      g.strokeEllipse(0, 0, r * 2, r * 2);
      g.setPosition(x, y - 4).setScale(0.1, 0.05).setAlpha(0.9);
      this.scene.tweens.add({
        targets: g,
        scaleX: 1,
        scaleY: 0.42,   // squash vertically → perspective / aura read
        alpha: 0,
        duration: dur,
        ease: 'Cubic.easeOut',
        onComplete: () => g.destroy(),
      });
    };
    makeWave(0xfbbf24, 34, 380);
    this.scene.time.delayedCall(70, () => makeWave(0xa16207, 26, 320));
  }

  /** Ancestral Guidance — Zhorak's empower burst. Golden column of light +
   *  outward rings from his feet so the buff activation reads at a glance. */
  playAncestralGuidance(x: number, y: number): void {
    // Three expanding rings, staggered, in warm gold.
    this.expandingRing(x, y, 0xfde68a, 80, 480);
    this.scene.time.delayedCall(80,  () => this.expandingRing(x, y, 0xfbbf24, 56, 420));
    this.scene.time.delayedCall(160, () => this.expandingRing(x, y, 0xf59e0b, 38, 360));
    // Vertical light column — tall thin rectangle that fades up, giving a "rays
    // from the ancestors" read.
    const col = this.scene.add.rectangle(x, y - 42, 26, 90, 0xfde68a, 0.55).setDepth(21);
    this.scene.tweens.add({
      targets: col,
      y: y - 80,
      scaleY: 1.3,
      alpha: 0,
      duration: 620,
      ease: 'Cubic.easeOut',
      onComplete: () => col.destroy(),
    });
    // Rising motes — small golden sparks trailing upward.
    if (this.scene.textures.exists('spark')) {
      this.burstParticles(x, y - 20, 'spark', 14, {
        speedY: { min: -120, max: -60 }, speedX: { min: -30, max: 30 }, lifespan: 700,
        scale: { start: 0.7, end: 0 }, tint: [0xfde68a, 0xfbbf24, 0xf59e0b],
        alpha: { start: 1, end: 0 },
      });
    }
  }

  /** Sylvari Piercing Shot — magic-pack lance projectile. Starts at the hero,
   *  travels to the target while looping the 8-frame bolt anim. The first
   *  frame spawns at the hero (origin 0, 0.5) and the last frame lands on the
   *  target via the position tween. */
  playPiercingBolt(fromX: number, fromY: number, toX: number, toY: number): void {
    // Prefer the new magic-spell-pack lance; fall back to the older bolt
    // sheet if that texture didn't load; final fallback to a plain arrow.
    const useSpellPack = this.scene.textures.exists('effect_spell_pierce')
      && this.scene.anims.exists('fx_spell_pierce');
    const useLegacy = !useSpellPack
      && this.scene.textures.exists('effect_pierce_bolt')
      && this.scene.anims.exists('fx_pierce_bolt');
    if (!useSpellPack && !useLegacy) {
      this.playArrow(fromX, fromY, toX, toY);
      return;
    }
    const textureKey = useSpellPack ? 'effect_spell_pierce' : 'effect_pierce_bolt';
    const animKey    = useSpellPack ? 'fx_spell_pierce'      : 'fx_pierce_bolt';
    const angle = Math.atan2(toY - fromY, toX - fromX);
    const dist = Math.hypot(toX - fromX, toY - fromY);
    const duration = Math.max(130, dist * 0.32);

    const bolt = this.scene.add.sprite(fromX, fromY, textureKey)
      .setDisplaySize(72, 36)
      .setDepth(18)
      .setRotation(angle)
      .setOrigin(0, 0.5);

    bolt.play(animKey);

    this.scene.tweens.add({
      targets: bolt,
      x: toX,
      y: toY,
      duration,
      ease: 'Linear',
      onComplete: () => {
        bolt.destroy();
        this.expandingRing(toX, toY, 0x60d0ff, 48, 280);
      },
    });
  }

  /** Frost nova — concentric icy rings radiating out. Used by the Archivist lich's AoE root. */
  playFrostNova(x: number, y: number): void {
    this.expandingRing(x, y, 0x8ad4ff, 180, 520);
    this.scene.time.delayedCall(90,  () => this.expandingRing(x, y, 0x60a0ff, 140, 420));
    this.scene.time.delayedCall(180, () => this.expandingRing(x, y, 0xe0f0ff, 100, 320));
  }

  /** Golden shimmer at caster — Mage Illumination speed buff */
  playMidas(x: number, y: number): void {
    this.playFxSprite(x, y, 'effect_midas', 'fx_midas', 56, 0.5);
  }

  /** Arcane Might — wide violet-arcane burst: expanding rune rings, ascending glyphs, star particles.
   *  Played on Zyphara when she casts the party-wide mana / resist buff. */
  playArcaneMight(x: number, y: number): void {
    // Central bright flash (additive)
    const flash = this.scene.add.circle(x, y, 42, 0xc0b0ff, 0.7).setDepth(19).setBlendMode(Phaser.BlendModes.ADD);
    this.scene.tweens.add({
      targets: flash, scale: 2.6, alpha: 0, duration: 520, ease: 'Cubic.easeOut',
      onComplete: () => flash.destroy(),
    });
    // Inner sapphire core
    const core = this.scene.add.circle(x, y, 20, 0x6080ff, 0.85).setDepth(19.1).setBlendMode(Phaser.BlendModes.ADD);
    this.scene.tweens.add({
      targets: core, scale: 2.0, alpha: 0, duration: 400, ease: 'Cubic.easeOut',
      onComplete: () => core.destroy(),
    });

    // Three staggered arcane rings — violet → blue → white
    this.expandingRing(x, y, 0x8040ff, 90, 620);
    this.scene.time.delayedCall(90,  () => this.expandingRing(x, y, 0x60a0ff, 70, 520));
    this.scene.time.delayedCall(180, () => this.expandingRing(x, y, 0xe0d0ff, 52, 420));

    // Rotating rune ring — a circle of 6 star glyphs that spins and fades
    const ringGroup = this.scene.add.container(x, y).setDepth(20);
    const runeChars = ['✦', '✧', '✦', '✧', '✦', '✧'];
    const runeRadius = 48;
    for (let k = 0; k < 6; k++) {
      const a = (k / 6) * Math.PI * 2;
      const rune = this.scene.add.text(Math.cos(a) * runeRadius, Math.sin(a) * runeRadius, runeChars[k], {
        fontSize: '18px', fontFamily: 'Palatino Linotype, Georgia, serif', fontStyle: 'bold',
        color: '#c0a0ff', stroke: '#1a0830', strokeThickness: 3,
      }).setOrigin(0.5);
      ringGroup.add(rune);
    }
    this.scene.tweens.add({
      targets: ringGroup,
      angle: 220,
      scale: 1.6,
      alpha: 0,
      duration: 900,
      ease: 'Cubic.easeOut',
      onComplete: () => ringGroup.destroy(),
    });

    // Star particles shooting outward
    this.burstParticles(x, y, 'projectile', 22, {
      speed: { min: 70, max: 200 },
      angle: { min: 0, max: 360 },
      scale: { start: 0.6, end: 0 },
      tint: [0xb080ff, 0x8040ff, 0xc0a0ff, 0xe0d0ff, 0xffffff],
      lifespan: 760,
    });

    // Ascending soft blue sparks
    this.burstParticles(x, y + 10, 'projectile', 14, {
      speed: { min: 40, max: 120 },
      angle: { min: 240, max: 300 },
      scale: { start: 0.45, end: 0 },
      tint: [0x80a0ff, 0xc0d0ff, 0xffffff],
      lifespan: 900,
      gravityY: -180,
    });

    // Rising rune glyph above caster
    const glyph = this.scene.add.text(x, y - 20, '✧', {
      fontSize: '38px', fontFamily: 'Palatino Linotype, Georgia, serif', fontStyle: 'bold',
      color: '#d0b0ff', stroke: '#1a0830', strokeThickness: 4,
    }).setOrigin(0.5).setDepth(22).setAlpha(0).setScale(0.5);
    this.scene.tweens.add({
      targets: glyph,
      alpha: { from: 0.95, to: 0 },
      y: y - 90,
      scale: { from: 1.0, to: 1.7 },
      duration: 900,
      ease: 'Cubic.easeOut',
      onStart: () => glyph.setAlpha(0.95),
      onComplete: () => glyph.destroy(),
    });
  }

  /** Sun beam strike — Tank Consecration / Ranger Eagle Eye */
  playSunStrike(x: number, y: number): void {
    this.playFxSprite(x, y, 'effect_sun_strike', 'fx_sun_strike', 64, 1.0);
  }

  /** Dark void on impact — Word of Death / Fear */
  playBlackHole(x: number, y: number): void {
    this.playFxSprite(x, y, 'effect_black_hole', 'fx_black_hole', 56, 0.5);
  }


  /** Poison cloud — animated green cloud at target feet (Kael Poison Cloud) */
  playPoisonCloud(x: number, y: number): void {
    this.playFxSprite(x, y, 'effect_poison_cloud', 'fx_poison_cloud', 80, 0.7);
  }

  /** Hemorrhage — blood drops falling on bleeding enemy (Whirlwind / Sinister Strike DoT) */
  playBleed(x: number, y: number): void {
    this.playFxSprite(x, y - 20, 'effect_hemorrhage', 'fx_hemorrhage', 64, 0.5);
  }

  /** Poison/DoT — green dripping particles downward */
  playPoisonDrip(x: number, y: number): void {
    this.burstParticles(x, y, 'projectile', 12, {
      speed: { min: 20, max: 60 },
      angle: { min: 240, max: 300 },
      scale: { start: 0.4, end: 0 },
      tint: [0x4ade80, 0x16a34a, 0x86efac],
      lifespan: 600,
      gravityY: 120,
    });
  }

  /** Ironheld's Taunt — bronze/ember shockwave with radiating slam lines + bellow ring.
   *  `radius` is the ability's taunt range; the ring expands to that radius. */
  playTaunt(x: number, y: number, radius: number = 450): void {
    // Ground slam dust + ember cloud at the hero's feet
    this.burstParticles(x, y + 8, 'projectile', 14, {
      speed: { min: 80, max: 180 },
      angle: { min: 200, max: 340 },
      scale: { start: 0.8, end: 0 },
      tint: [0x3a2410, 0x6b3a12, 0x8b5028, 0xcfa05a],
      lifespan: 520,
      gravityY: 180,
    });

    // Three expanding bronze shockwaves — tight, mid, wide
    const g1 = this.scene.add.graphics().setDepth(20);
    g1.lineStyle(4, 0xff8030, 1);
    g1.strokeCircle(0, 0, radius);
    g1.setPosition(x, y).setScale(0.04).setAlpha(0.95);
    this.scene.tweens.add({
      targets: g1, scaleX: 1, scaleY: 1, alpha: 0, duration: 520, ease: 'Cubic.easeOut',
      onComplete: () => g1.destroy(),
    });
    const g2 = this.scene.add.graphics().setDepth(20);
    g2.lineStyle(3, 0xffd060, 0.9);
    g2.strokeCircle(0, 0, radius * 0.72);
    g2.setPosition(x, y).setScale(0.04).setAlpha(0.9);
    this.scene.tweens.add({
      targets: g2, scaleX: 1, scaleY: 1, alpha: 0, duration: 440, ease: 'Cubic.easeOut',
      onComplete: () => g2.destroy(),
    });
    const g3 = this.scene.add.graphics().setDepth(20);
    g3.lineStyle(2, 0xffe8a0, 0.85);
    g3.strokeCircle(0, 0, radius * 0.45);
    g3.setPosition(x, y).setScale(0.04).setAlpha(0.9);
    this.scene.tweens.add({
      targets: g3, scaleX: 1, scaleY: 1, alpha: 0, duration: 360, ease: 'Cubic.easeOut',
      onComplete: () => g3.destroy(),
    });

    // Radiating slam lines — 8 spokes that shoot out and fade
    const spokes = this.scene.add.graphics().setDepth(19.5);
    spokes.setPosition(x, y).setAlpha(0.95);
    spokes.lineStyle(3, 0xffb060, 1);
    for (let k = 0; k < 8; k++) {
      const a = (k / 8) * Math.PI * 2;
      const r0 = 24;
      const r1 = radius * 0.85;
      spokes.beginPath();
      spokes.moveTo(Math.cos(a) * r0, Math.sin(a) * r0);
      spokes.lineTo(Math.cos(a) * r1, Math.sin(a) * r1);
      spokes.strokePath();
    }
    this.scene.tweens.add({
      targets: spokes, alpha: 0, duration: 380, ease: 'Cubic.easeOut',
      onComplete: () => spokes.destroy(),
    });

    // Shield glyph rising above the caster — signifies the defensive buff
    const shield = this.scene.add.text(x, y - 20, '⛨', {
      fontSize: '34px', fontFamily: 'Palatino Linotype, Georgia, serif', fontStyle: 'bold',
      color: '#ffd070', stroke: '#2a1000', strokeThickness: 4,
    }).setOrigin(0.5).setDepth(22).setAlpha(0).setScale(0.5);
    this.scene.tweens.add({
      targets: shield,
      alpha: { from: 0.95, to: 0 },
      y: y - 72,
      scale: { from: 0.95, to: 1.5 },
      duration: 760,
      ease: 'Cubic.easeOut',
      onStart: () => shield.setAlpha(0.95),
      onComplete: () => shield.destroy(),
    });
  }

  /** Per-enemy taunt mark — red glare flash + crossed-swords icon fades over the enemy's head */
  playTauntMark(x: number, y: number): void {
    // Red flash behind the enemy
    const flash = this.scene.add.circle(x, y, 28, 0xff4020, 0.4).setDepth(18).setBlendMode(Phaser.BlendModes.ADD);
    this.scene.tweens.add({
      targets: flash, scale: 2.2, alpha: 0, duration: 320, ease: 'Cubic.easeOut',
      onComplete: () => flash.destroy(),
    });
    // Rising ember glyph — crossed swords
    const mark = this.scene.add.text(x, y - 18, '⚔', {
      fontSize: '22px', fontFamily: 'Palatino Linotype, Georgia, serif', fontStyle: 'bold',
      color: '#ff8030', stroke: '#2a0800', strokeThickness: 4,
    }).setOrigin(0.5).setDepth(22).setAlpha(0).setScale(0.4);
    this.scene.tweens.add({
      targets: mark,
      alpha: { from: 0.95, to: 0 },
      y: y - 52,
      scale: { from: 0.9, to: 1.3 },
      duration: 620,
      ease: 'Cubic.easeOut',
      onStart: () => mark.setAlpha(0.95),
      onComplete: () => mark.destroy(),
    });
    // A few ember sparks
    this.burstParticles(x, y - 10, 'projectile', 6, {
      speed: { min: 40, max: 110 },
      angle: { min: 230, max: 310 },
      scale: { start: 0.5, end: 0 },
      tint: [0xff4020, 0xff8030, 0xffc060],
      lifespan: 420,
      gravityY: -40,
    });
  }

  /** Buff/Aura — golden spinning particles around hero */
  playBuffAura(x: number, y: number): void {
    this.burstParticles(x, y, 'projectile', 14, {
      speed: { min: 30, max: 70 },
      angle: { min: 0, max: 360 },
      scale: { start: 0.5, end: 0 },
      tint: [0xfbbf24, 0xf59e0b, 0xfef9c3],
      lifespan: 700,
      gravityY: -60,
    });
  }

  /** Fear wave — ghostly skull-like apparition rising over the target with violet haze + eye glow. */
  playFearWave(x: number, y: number): void {
    // Expanding shadow rings — outer dark, inner violet glow
    this.expandingRing(x, y, 0x1a0a30, 80, 500);
    this.scene.time.delayedCall(60, () => this.expandingRing(x, y, 0x5b21b6, 60, 420));
    this.scene.time.delayedCall(120, () => this.expandingRing(x, y, 0xa855f7, 42, 360));

    // Ghostly wisps — upward drifting violet particles
    this.burstParticles(x, y, 'projectile', 14, {
      speed: { min: 60, max: 150 },
      angle: { min: 210, max: 330 },
      scale: { start: 0.55, end: 0 },
      tint: [0x6b21a8, 0xa855f7, 0xc084fc, 0xe9d5ff],
      lifespan: 650,
      gravityY: -120,
    });

    // Apparition: a small skull glyph that rises and fades above the target
    const skull = this.scene.add.text(x, y - 20, '☠', {
      fontSize: '32px', fontFamily: 'Palatino Linotype, Georgia, serif', fontStyle: 'bold',
      color: '#a855f7', stroke: '#1a0530', strokeThickness: 4,
    }).setOrigin(0.5).setDepth(22).setAlpha(0).setScale(0.4);
    this.scene.tweens.add({
      targets: skull,
      alpha: { from: 0.9, to: 0 },
      y: y - 80,
      scale: { from: 0.8, to: 1.4 },
      duration: 900,
      ease: 'Cubic.easeOut',
      onStart: () => skull.setAlpha(0.9),
      onComplete: () => skull.destroy(),
    });

    // Eye-glow dots — two small glowing purple orbs that flash briefly
    for (const dx of [-8, 8]) {
      const eye = this.scene.add.circle(x + dx, y - 28, 2.5, 0xff40a0, 1)
        .setDepth(23).setBlendMode(Phaser.BlendModes.ADD);
      this.scene.tweens.add({
        targets: eye, alpha: 0, scale: 2.2, duration: 380,
        onComplete: () => eye.destroy(),
      });
    }
  }

  /** Resurrect pillar — dramatic golden pillar of light + shockwave rings + ascending angels of sparks. */
  playResurrectPillar(x: number, y: number): void {
    // Ground halo — bright disc at feet
    const disc = this.scene.add.ellipse(x, y + 2, 90, 28, 0xfde68a, 0.9).setDepth(17).setBlendMode(Phaser.BlendModes.ADD);
    this.scene.tweens.add({
      targets: disc, scaleX: 1.5, scaleY: 1.5, alpha: 0, duration: 780, ease: 'Cubic.easeOut',
      onComplete: () => disc.destroy(),
    });
    // Vertical light pillar — tall rectangle rising from the ground
    const pillar = this.scene.add.rectangle(x, y - 160, 44, 180, 0xfef3c7, 0.55)
      .setDepth(18).setOrigin(0.5, 0).setBlendMode(Phaser.BlendModes.ADD);
    this.scene.tweens.add({
      targets: pillar, alpha: 0, scaleX: 0.35, duration: 1100, ease: 'Cubic.easeIn',
      onComplete: () => pillar.destroy(),
    });
    // Bright inner core pillar
    const core = this.scene.add.rectangle(x, y - 160, 18, 180, 0xffffff, 0.85)
      .setDepth(18.1).setOrigin(0.5, 0).setBlendMode(Phaser.BlendModes.ADD);
    this.scene.tweens.add({
      targets: core, alpha: 0, scaleX: 0.25, duration: 900, ease: 'Cubic.easeIn',
      onComplete: () => core.destroy(),
    });

    // Two staggered golden shockwave rings on the ground
    this.expandingRing(x, y + 2, 0xfbbf24, 70, 560);
    this.scene.time.delayedCall(130, () => this.expandingRing(x, y + 2, 0xfef3c7, 50, 460));

    // Fast upward column of golden sparks (original)
    this.burstParticles(x, y, 'projectile', 28, {
      speed: { min: 50, max: 160 },
      angle: { min: 260, max: 280 },
      scale: { start: 0.65, end: 0 },
      tint: [0xfbbf24, 0xfef3c7, 0xfde68a, 0xffffff],
      lifespan: 1000,
      gravityY: -240,
    });
    // Wider burst of golden motes in all directions
    this.burstParticles(x, y, 'projectile', 18, {
      speed: { min: 60, max: 180 },
      angle: { min: 0, max: 360 },
      scale: { start: 0.55, end: 0 },
      tint: [0xfbbf24, 0xfef3c7, 0xffffff],
      lifespan: 820,
    });
    // Rising feather-like drift (slow, graceful sparks floating up)
    this.burstParticles(x, y - 20, 'projectile', 10, {
      speed: { min: 20, max: 50 },
      angle: { min: 250, max: 290 },
      scale: { start: 0.4, end: 0 },
      tint: [0xffffff, 0xfef3c7],
      lifespan: 1400,
      gravityY: -60,
    });

    // Rising cross glyph above the resurrected hero
    const cross = this.scene.add.text(x, y - 30, '✛', {
      fontSize: '40px', fontFamily: 'Palatino Linotype, Georgia, serif', fontStyle: 'bold',
      color: '#fef3c7', stroke: '#5a3600', strokeThickness: 5,
    }).setOrigin(0.5).setDepth(22).setAlpha(0).setScale(0.5);
    this.scene.tweens.add({
      targets: cross,
      alpha: { from: 1, to: 0 },
      y: y - 120,
      scale: { from: 1.0, to: 1.7 },
      duration: 1100,
      ease: 'Cubic.easeOut',
      onStart: () => cross.setAlpha(1),
      onComplete: () => cross.destroy(),
    });
  }

  /** Poison arrow — dark green glowing projectile from orc ranged enemy to hero */
  playPoisonArrow(fromX: number, fromY: number, toX: number, toY: number): void {
    const angle = Math.atan2(toY - fromY, toX - fromX);

    // Glow layer (wide, transparent outer green halo)
    const glow = this.scene.add.graphics().setDepth(17);
    glow.lineStyle(6, 0x16a34a, 0.25);
    glow.beginPath(); glow.moveTo(-12, 0); glow.lineTo(8, 0); glow.strokePath();
    glow.setPosition(fromX, fromY).setRotation(angle);

    // Main arrow shaft (dark green, thin)
    const g = this.scene.add.graphics().setDepth(18);
    g.lineStyle(2, 0x15803d, 1);
    g.beginPath(); g.moveTo(-10, 0); g.lineTo(5, 0); g.strokePath();
    // Arrowhead (bright toxic green)
    g.fillStyle(0x4ade80, 1);
    g.fillTriangle(3, -3, 3, 3, 13, 0);
    g.setPosition(fromX, fromY).setRotation(angle);

    const duration = 240;
    this.scene.tweens.add({ targets: glow, x: toX, y: toY, duration, ease: 'Linear', onComplete: () => glow.destroy() });
    this.scene.tweens.add({ targets: g,    x: toX, y: toY, duration, ease: 'Linear', onComplete: () => g.destroy() });
  }

  /** Arrow projectile from hero to target — drawn as a rotated shaft+head graphic */
  playArrow(fromX: number, fromY: number, toX: number, toY: number): void {
    const angle = Math.atan2(toY - fromY, toX - fromX);
    const useSprite = this.scene.textures.exists('arrow_icon43');
    if (useSprite) {
      // arrow_icon43's natural tip leans up-right by ~30° (not a clean 45°). Offset the sprite
      // rotation so its shaft stays roughly parallel to the travel vector.
      const ARROW_SPRITE_OFFSET = Math.PI / 6;
      const arrow = this.scene.add.image(fromX, fromY, 'arrow_icon43')
        .setDisplaySize(22, 6).setDepth(18).setRotation(angle + ARROW_SPRITE_OFFSET);
      this.scene.tweens.add({
        targets: arrow,
        x: toX,
        y: toY,
        duration: 200,
        ease: 'Linear',
        onComplete: () => arrow.destroy(),
      });
    } else {
      // Fallback: drawn arrow
      const g = this.scene.add.graphics().setDepth(18);
      g.lineStyle(2, 0xd4a017, 1);
      g.beginPath();
      g.moveTo(-10, 0);
      g.lineTo(5, 0);
      g.strokePath();
      g.fillStyle(0xfbbf24, 1);
      g.fillTriangle(3, -3, 3, 3, 13, 0);
      g.setPosition(fromX, fromY);
      g.setRotation(angle);
      this.scene.tweens.add({
        targets: g,
        x: toX,
        y: toY,
        duration: 220,
        ease: 'Linear',
        onComplete: () => g.destroy(),
      });
    }
  }

  /** Multishot — parallel arrows to multiple targets */
  playMultishot(fromX: number, fromY: number, targets: { x: number; y: number }[]): void {
    for (const t of targets) {
      const arrow = this.scene.add.image(fromX, fromY, 'projectile')
        .setDepth(18).setScale(0.5).setTint(0x86efac);
      this.scene.tweens.add({
        targets: arrow,
        x: t.x,
        y: t.y,
        duration: 260,
        ease: 'Linear',
        onComplete: () => arrow.destroy(),
      });
    }
  }

  /** Frost shield — blue expanding ring + sparkle particles */
  playFrostRing(x: number, y: number): void {
    this.expandingRing(x, y, 0x93c5fd, 55, 380);
    this.burstParticles(x, y, 'projectile', 12, {
      speed: { min: 40, max: 90 },
      angle: { min: 0, max: 360 },
      scale: { start: 0.35, end: 0 },
      tint: [0x93c5fd, 0xe0f2fe, 0x38bdf8],
      lifespan: 420,
    });
  }

  /** Evasion — speed-line blur burst to the left */
  playEvasionBlur(x: number, y: number): void {
    this.burstParticles(x, y, 'projectile', 8, {
      speed: { min: 80, max: 200 },
      angle: { min: 150, max: 210 },
      scale: { start: 0.45, end: 0 },
      tint: [0x94a3b8, 0xcbd5e1, 0xffffff],
      lifespan: 200,
    });
  }

  /** Reflect shield — purple spinning particles */
  playReflectShield(x: number, y: number): void {
    this.burstParticles(x, y, 'projectile', 16, {
      speed: { min: 45, max: 100 },
      angle: { min: 0, max: 360 },
      scale: { start: 0.5, end: 0 },
      tint: [0x2060a0, 0x4090c0, 0xa0d0f0],
      lifespan: 500,
    });
  }

  /** Melee spark — small quick metal flash for warrior/tank melee hits */
  playMeleeSpark(x: number, y: number): void {
    this.burstParticles(x, y, 'projectile', 4, {
      speed: { min: 50, max: 100 },
      angle: { min: 0, max: 360 },
      scale: { start: 0.25, end: 0 },
      tint: [0xfbbf24, 0xffffff, 0xfde68a],
      lifespan: 150,
    });
  }

  /** Traveling magic bolt from caster (mage/healer) to target — colored by class tint */
  playMagicBolt(fromX: number, fromY: number, toX: number, toY: number, tint: number = 0x4090c0): void {
    const bolt = this.scene.add.image(fromX, fromY, 'projectile')
      .setDepth(18).setScale(0.5).setTint(tint);
    this.scene.tweens.add({
      targets: bolt,
      x: toX,
      y: toY,
      duration: 200,
      ease: 'Linear',
      onComplete: () => {
        bolt.destroy();
        // Small impact burst at target
        this.burstParticles(toX, toY, 'projectile', 4, {
          speed: { min: 20, max: 55 },
          angle: { min: 0, max: 360 },
          scale: { start: 0.25, end: 0 },
          tint: [tint, 0xffffff],
          lifespan: 180,
        });
      },
    });
  }

  /** Caster impact — small light flash at target for mage/healer off-hand hits */
  playCasterImpact(x: number, y: number): void {
    this.burstParticles(x, y, 'projectile', 5, {
      speed: { min: 20, max: 60 },
      angle: { min: 0, max: 360 },
      scale: { start: 0.3, end: 0 },
      tint: [0x4090c0, 0xa0d0f0, 0xffffff],
      lifespan: 200,
    });
  }

  /** Thrown weapon projectile (knife, shuriken) from hero to target using item icon */
  playThrownWeapon(fromX: number, fromY: number, toX: number, toY: number, textureKey?: string): void {
    const key = textureKey && this.scene.textures.exists(textureKey) ? textureKey : 'projectile';
    const proj = this.scene.add.image(fromX, fromY, key)
      .setDepth(18).setDisplaySize(16, 16);
    this.scene.tweens.add({
      targets: proj,
      x: toX,
      y: toY,
      angle: 360,
      duration: 240,
      ease: 'Linear',
      onComplete: () => proj.destroy(),
    });
  }

  /** Death Coil — green skull orb travels from Morveth to target, bursts with necrotic particles */
  playDeathCoilBall(fromX: number, fromY: number, toX: number, toY: number): void {
    const texKey = 'effect_death_coil';
    const animKey = 'fx_death_coil';
    // Caster glow — quick ring pulse at the hero to read as "cast origin"
    this.expandingRing(fromX, fromY, 0x7c3aed, 32, 280);
    if (!this.scene.textures.exists(texKey)) {
      this.playMagicBolt(fromX, fromY, toX, toY, 0x7c3aed);
      return;
    }
    const sprite = this.scene.add.sprite(fromX, fromY, texKey)
      .setDisplaySize(56, 56).setDepth(20).setOrigin(0.5);
    if (this.scene.anims.exists(animKey)) sprite.play(animKey);
    // Shadow trail — purple wisps left along the ball's path every 60ms
    const trailTimer = this.scene.time.addEvent({
      delay: 45, loop: true,
      callback: () => {
        if (!sprite.active) return;
        const wisp = this.scene.add.circle(sprite.x, sprite.y, 6, 0x6b21a8, 0.55)
          .setDepth(19).setBlendMode(Phaser.BlendModes.ADD);
        this.scene.tweens.add({
          targets: wisp, alpha: 0, scale: 0.2, duration: 320,
          onComplete: () => wisp.destroy(),
        });
      },
    });
    this.scene.tweens.add({
      targets: sprite,
      x: toX, y: toY,
      duration: 380, ease: 'Sine.easeIn',
      onComplete: () => {
        trailTimer.remove(false);
        sprite.destroy();
        // Impact — darker violet ring + inner burst + soul shard streaks
        this.expandingRing(toX, toY, 0x3b0764, 44, 320);
        this.expandingRing(toX, toY, 0xa855f7, 28, 260);
        this.burstParticles(toX, toY, 'projectile', 12, {
          speed: { min: 40, max: 110 },
          angle: { min: 0, max: 360 },
          scale: { start: 0.5, end: 0 },
          tint: [0x6b21a8, 0xa855f7, 0xc084fc, 0xffffff],
          lifespan: 420,
        });
      },
    });
  }

  /** Plague spread — single dark spore cloud over an affected enemy (used by Death Coil rank 5). */
  playPlagueSpread(x: number, y: number): void {
    this.expandingRing(x, y, 0x3b0764, 32, 480);
    this.burstParticles(x, y, 'projectile', 10, {
      speed: { min: 15, max: 55 },
      angle: { min: 180, max: 360 },
      scale: { start: 0.35, end: 0 },
      tint: [0x4a044e, 0x6b21a8, 0x86198f],
      lifespan: 700,
      gravityY: -40,
    });
    // Small ominous skull rising
    const skull = this.scene.add.text(x, y - 8, '☠', {
      fontSize: '16px', fontFamily: 'Palatino Linotype, Georgia, serif', color: '#a855f7',
      stroke: '#0a0210', strokeThickness: 3,
    }).setOrigin(0.5).setDepth(21).setAlpha(0);
    this.scene.tweens.add({
      targets: skull,
      alpha: { from: 0.85, to: 0 },
      y: y - 38,
      scale: { from: 0.7, to: 1.1 },
      duration: 700, ease: 'Cubic.easeOut',
      onStart: () => skull.setAlpha(0.85),
      onComplete: () => skull.destroy(),
    });
  }

  /** Hellfire Ball — animated fire orb travels from Demon Overlord to a target hero, bursts on impact */
  playHellfireBall(fromX: number, fromY: number, toX: number, toY: number): void {
    const texKey = 'effect_hellfire';
    const animKey = 'fx_hellfire';
    if (!this.scene.textures.exists(texKey)) {
      this.playMagicBolt(fromX, fromY, toX, toY, 0xdc2626);
      return;
    }
    const sprite = this.scene.add.sprite(fromX, fromY, texKey)
      .setDisplaySize(48, 48).setDepth(20).setOrigin(0.5);
    if (this.scene.anims.exists(animKey)) sprite.play(animKey);
    this.scene.tweens.add({
      targets: sprite,
      x: toX,
      y: toY,
      duration: 280,
      ease: 'Linear',
      onComplete: () => {
        sprite.destroy();
        this.playExplosionBurst(toX, toY);
      },
    });
  }

  /** Void Lance projectile — animated lance sprite travels to farthest hero, impacts with purple burst */
  playVoidLance(fromX: number, fromY: number, toX: number, toY: number): void {
    const texKey = 'effect_void_lance';
    const animKey = 'fx_void_lance';
    if (!this.scene.textures.exists(texKey)) {
      // Fallback to tinted magic bolt if texture not loaded
      this.playMagicBolt(fromX, fromY, toX, toY, 0x5b21b6);
      return;
    }
    const angle = Math.atan2(toY - fromY, toX - fromX);
    const sprite = this.scene.add.sprite(fromX, fromY, texKey)
      .setDisplaySize(96, 96).setDepth(20).setOrigin(0.5).setRotation(angle);
    if (this.scene.anims.exists(animKey)) sprite.play(animKey);
    this.scene.tweens.add({
      targets: sprite,
      x: toX,
      y: toY,
      duration: 380,
      ease: 'Linear',
      onComplete: () => {
        sprite.destroy();
        // Impact: purple burst + expanding ring
        this.expandingRing(toX, toY, 0x1a4878, 40, 300);
        this.burstParticles(toX, toY, 'projectile', 10, {
          speed: { min: 40, max: 110 },
          angle: { min: 0, max: 360 },
          scale: { start: 0.45, end: 0 },
          tint: [0x1a4878, 0x4090c0, 0xa0d0f0],
          lifespan: 280,
        });
      },
    });
  }

  /** Fireball projectile: orange orb travels from mage to target, explodes on impact */
  playFireball(fromX: number, fromY: number, toX: number, toY: number): void {
    const sprite = this.scene.add.sprite(fromX, fromY, 'effect_fireball')
      .setDisplaySize(32, 32).setDepth(20).setOrigin(0.5);
    if (this.scene.anims.exists('fx_fireball')) sprite.play('fx_fireball');
    this.scene.tweens.add({
      targets: sprite,
      x: toX,
      y: toY,
      duration: 280,
      ease: 'Linear',
      onComplete: () => {
        sprite.destroy();
        this.playExplosionBurst(toX, toY);
      },
    });
  }

  /** Explosion burst — orange/red particle burst for fireball impact */
  playExplosionBurst(x: number, y: number): void {
    this.playFxSprite(x, y, 'effect_explosion', 'fx_explosion', 64, 0.5);
    this.burstParticles(x, y, 'projectile', 10, {
      speed: { min: 60, max: 150 },
      angle: { min: 0, max: 360 },
      scale: { start: 0.5, end: 0 },
      tint: [0xf97316, 0xef4444, 0xfbbf24],
      lifespan: 320,
    });
  }

  /** Eruption — lava column rising from enemy feet + heat ring + ground lava pool.
   *  Previous version anchored at originY=1.0 which cut the bottom; now anchors lower so the full column reads. */
  playEruption(x: number, y: number): void {
    // Ground lava pool — dark ellipse under feet
    const pool = this.scene.add.ellipse(x, y + 4, 80, 26, 0x7a1500, 0.85).setDepth(16);
    this.scene.tweens.add({
      targets: pool, scaleX: 1.2, scaleY: 1.2, alpha: 0, duration: 760, ease: 'Cubic.easeOut',
      onComplete: () => pool.destroy(),
    });
    // Bright inner lava core
    const core = this.scene.add.ellipse(x, y + 4, 60, 18, 0xff8030, 0.9).setDepth(16.1).setBlendMode(Phaser.BlendModes.ADD);
    this.scene.tweens.add({
      targets: core, scaleX: 1.4, scaleY: 1.4, alpha: 0, duration: 520, ease: 'Cubic.easeOut',
      onComplete: () => core.destroy(),
    });
    // Fire column — anchored at 0.85 so the bottom of the animation is fully visible
    this.playFxSprite(x, y + 8, 'effect_fire_wall', 'fx_fire_wall', 110, 0.85);
    // Expanding lava ring at ground level (outer dark, inner bright)
    this.expandingRing(x, y + 4, 0x7a1000, 70, 460);
    this.scene.time.delayedCall(60, () => this.expandingRing(x, y + 4, 0xdc2626, 55, 380));
    this.scene.time.delayedCall(120, () => this.expandingRing(x, y + 4, 0xfbbf24, 38, 320));
    // Rising fire ember particles
    this.burstParticles(x, y - 6, 'projectile', 18, {
      speed: { min: 70, max: 190 },
      angle: { min: 230, max: 310 },
      scale: { start: 0.7, end: 0 },
      tint: [0xf97316, 0xef4444, 0xfbbf24, 0xfde68a, 0xffffff],
      lifespan: 620,
      gravityY: -140,
    });
    // Molten spatter — bright bits that arc outward low
    this.burstParticles(x, y + 2, 'projectile', 10, {
      speed: { min: 40, max: 110 },
      angle: { min: 200, max: 340 },
      scale: { start: 0.45, end: 0 },
      tint: [0xff4020, 0xffa030],
      lifespan: 500,
      gravityY: 120,
    });
  }

  /** Magma Shield — rotating ring of fire wrapped around the caster + pulsing aura.
   *  Registers three orbiting flame sprites plus a sustained ember emitter; lifetime ~1.6s. */
  playFireWall(x: number, y: number): void {
    // Dark orange base ring + bright inner ring (instant burst)
    const base = this.scene.add.circle(x, y, 50, 0xf97316, 0.45).setDepth(17).setBlendMode(Phaser.BlendModes.ADD);
    this.scene.tweens.add({ targets: base, scale: 1.35, alpha: 0, duration: 560, onComplete: () => base.destroy() });
    const inner = this.scene.add.circle(x, y, 34, 0xfbbf24, 0.55).setDepth(17.1).setBlendMode(Phaser.BlendModes.ADD);
    this.scene.tweens.add({ targets: inner, scale: 1.6, alpha: 0, duration: 440, onComplete: () => inner.destroy() });

    // Three orbiting flame orbs circling the caster for ~1.5s
    const orbDuration = 1500;
    for (let k = 0; k < 3; k++) {
      const baseAngle = (k / 3) * Math.PI * 2;
      const orb = this.scene.add.circle(x, y, 6, 0xff8030, 0.9).setDepth(19).setBlendMode(Phaser.BlendModes.ADD);
      const state = { t: 0 };
      this.scene.tweens.add({
        targets: state, t: 1, duration: orbDuration, ease: 'Linear',
        onUpdate: () => {
          const a = baseAngle + state.t * Math.PI * 4; // two full revolutions
          orb.x = x + Math.cos(a) * 46;
          orb.y = y + Math.sin(a) * 24; // flatten into ellipse for waist-height feel
          orb.alpha = 0.9 * (1 - state.t * 0.6);
          orb.scale = 1 + Math.sin(state.t * Math.PI * 3) * 0.3;
        },
        onComplete: () => orb.destroy(),
      });
    }

    // Expanding outer heat rings (two staggered)
    this.expandingRing(x, y, 0xff4020, 64, 520);
    this.scene.time.delayedCall(140, () => this.expandingRing(x, y, 0xfbbf24, 46, 420));

    // Sustained upward ember trail
    this.burstParticles(x, y + 6, 'projectile', 18, {
      speed: { min: 40, max: 120 },
      angle: { min: 210, max: 330 },
      scale: { start: 0.55, end: 0 },
      tint: [0xf97316, 0xef4444, 0xfbbf24, 0xfde68a],
      lifespan: 760,
      gravityY: -80,
    });
    // Low-flying ember spray to sell the "wall of flame" shape
    this.burstParticles(x, y, 'projectile', 12, {
      speed: { min: 60, max: 140 },
      angle: { min: 0, max: 360 },
      scale: { start: 0.45, end: 0 },
      tint: [0xff4020, 0xff8030, 0xfbbf24],
      lifespan: 520,
    });

    // Rising shield glyph above the caster
    const glyph = this.scene.add.text(x, y - 10, '⛨', {
      fontSize: '34px', fontFamily: 'Palatino Linotype, Georgia, serif', fontStyle: 'bold',
      color: '#ffb060', stroke: '#2a0a00', strokeThickness: 4,
    }).setOrigin(0.5).setDepth(22).setAlpha(0).setScale(0.5);
    this.scene.tweens.add({
      targets: glyph,
      alpha: { from: 0.95, to: 0 },
      y: y - 70,
      scale: { from: 0.95, to: 1.5 },
      duration: 820,
      ease: 'Cubic.easeOut',
      onStart: () => glyph.setAlpha(0.95),
      onComplete: () => glyph.destroy(),
    });
  }

  /**
   * Demon_1 boss laser beam — fires a channeled violet beam from (fromX,fromY)
   * sweeping left to the hero group.
   *
   * Phases:
   *  0 – 500 ms  : charge-up orb VFX at caster
   *  500 – 2500 ms: beam active — animated sprites tiled across beam + glow line
   *
   * Returns a cleanup function the caller must invoke when done (in case of early cancel).
   */
  playDemonLaserBeam(fromX: number, fromY: number, toX: number, toY: number): () => void {
    const VIOLET      = 0x1a4878;
    const VIOLET_CORE = 0xa0d0f0;
    const BEAM_Y      = fromY;

    // ── Charge-up orb ────────────────────────────────────────────────────────
    const chargeOrbs: Phaser.GameObjects.Sprite[] = [];
    for (let i = 0; i < 3; i++) {
      const orb = this.scene.add.sprite(fromX - 10 + i * 8, BEAM_Y, 'effect_demon_charge')
        .setTint(VIOLET).setDisplaySize(48, 48).setDepth(18).setAlpha(0.85);
      if (this.scene.anims.exists('fx_demon_charge')) orb.play('fx_demon_charge');
      else this.scene.tweens.add({ targets: orb, alpha: 0, duration: 500, onComplete: () => orb.destroy() });
      chargeOrbs.push(orb);
    }
    // Expanding ring at cast origin
    this.expandingRing(fromX, BEAM_Y, VIOLET, 40, 450);

    // ── Beam fires after 500 ms windup ───────────────────────────────────────
    const beamSprites:  Phaser.GameObjects.Sprite[]   = [];
    const beamGraphics: Phaser.GameObjects.Graphics[] = [];
    let cleanedUp = false;

    const fireTimer = this.scene.time.delayedCall(500, () => {
      chargeOrbs.forEach((o) => o.destroy());

      const beamLength = Math.abs(fromX - toX);

      // Graphics beam core (drawn once, stays for beam duration)
      const g = this.scene.add.graphics().setDepth(15);
      g.lineStyle(18, VIOLET,      0.15);  g.lineBetween(fromX, BEAM_Y, toX, BEAM_Y);
      g.lineStyle(10, VIOLET,      0.30);  g.lineBetween(fromX, BEAM_Y, toX, BEAM_Y);
      g.lineStyle(4,  0x4090c0,    0.65);  g.lineBetween(fromX, BEAM_Y, toX, BEAM_Y);
      g.lineStyle(1,  VIOLET_CORE, 0.95);  g.lineBetween(fromX, BEAM_Y, toX, BEAM_Y);
      beamGraphics.push(g);

      // Tiled animated beam sprites from caster toward heroes every 64 px
      const frameSize = 64;
      const stepCount = Math.ceil(beamLength / frameSize) + 1;
      for (let i = 0; i < stepCount; i++) {
        const sx = fromX - i * frameSize;
        if (sx < toX - frameSize) break;
        const spr = this.scene.add.sprite(sx, BEAM_Y, 'effect_demon_laser')
          .setTint(VIOLET).setDisplaySize(frameSize, frameSize).setDepth(16).setAlpha(0.8);
        if (this.scene.anims.exists('fx_demon_laser')) spr.play('fx_demon_laser');
        beamSprites.push(spr);
      }

      // Impact burst particles continuously at the far end
      const impactEmitter = this.scene.add.particles(toX, BEAM_Y, 'projectile', {
        speed: { min: 30, max: 90 },
        angle: { min: 130, max: 230 },
        scale: { start: 0.4, end: 0 },
        tint: [VIOLET, 0x4090c0, VIOLET_CORE],
        lifespan: 350,
        frequency: 50,
        quantity: 3,
      }).setDepth(19);
      beamGraphics.push(impactEmitter as unknown as Phaser.GameObjects.Graphics);

      // Camera flash on beam start
      this.scene.cameras.main.flash(80, 80, 0, 160, false);
    });

    const cleanup = (): void => {
      if (cleanedUp) return;
      cleanedUp = true;
      fireTimer.remove(false);
      chargeOrbs.forEach((o) => { try { o.destroy(); } catch (_) {} });
      beamSprites.forEach((s) => { try { s.destroy(); } catch (_) {} });
      beamGraphics.forEach((g) => { try { g.destroy(); } catch (_) {} });
    };

    return cleanup;
  }

  // ── Lava Golem boss spell VFX ─────────────────────────────────────────────

  /** Molten Shell activate — fire shield flare at boss position (Fire_shield, 8f 128×128) */
  playMoltenShellActivate(x: number, y: number): void {
    this.playFxSprite(x, y - 40, 'effect_fire_shield', 'fx_fire_shield', 120, 0.5);
    this.expandingRing(x, y, 0xff4500, 60, 400);
  }

  /** Magma Hurl projectile — lava drop travels from boss to target hero (Lava_drop, 16f 64×64) */
  playLavaDrop(fromX: number, fromY: number, toX: number, toY: number): void {
    const texKey = 'effect_lava_drop';
    const animKey = 'fx_lava_drop';
    if (!this.scene.textures.exists(texKey)) {
      this.playMagicBolt(fromX, fromY, toX, toY, 0xf97316);
      return;
    }
    const sprite = this.scene.add.sprite(fromX, fromY, texKey)
      .setDisplaySize(56, 56).setDepth(20).setOrigin(0.5);
    if (this.scene.anims.exists(animKey)) sprite.play(animKey);
    this.scene.tweens.add({
      targets: sprite,
      x: toX,
      y: toY,
      duration: 320,
      ease: 'Linear',
      onComplete: () => {
        sprite.destroy();
        this.playExplosionBurst(toX, toY);
      },
    });
  }

  /** Inferno Wave — fire hurricane AoE at target position (Fire_hurracane, 14f 256×256) */
  playFireHurricane(x: number, y: number): void {
    const texKey = 'effect_fire_hurracane';
    const animKey = 'fx_fire_hurracane';
    if (!this.scene.textures.exists(texKey)) {
      this.playEruption(x, y);
      return;
    }
    const sprite = this.scene.add.sprite(x, y - 40, texKey)
      .setDisplaySize(160, 160).setDepth(25).setOrigin(0.5);
    if (this.scene.anims.exists(animKey)) {
      sprite.play(animKey);
      sprite.once('animationcomplete', () => sprite.destroy());
    } else {
      this.scene.tweens.add({ targets: sprite, alpha: 0, duration: 400, onComplete: () => sprite.destroy() });
    }
    // Heat ring at ground level
    this.expandingRing(x, y, 0xdc2626, 70, 500);
  }

  /**
   * Wraith Strike teleport sequence — rogue phases to target, strikes, phases back.
   * Call this at the target's position. Plays vanish-at-target, then after onReturn
   * callback fires you should move the hero sprite back and play materialise there.
   *
   * Usage: call playShadowTeleportOut at hero base first, then after ~250ms call
   * playShadowTeleportIn at target and deal damage, then after ~400ms call
   * playShadowTeleportOut at target and playShadowTeleportIn at base.
   */
  /** Regular spear throw — smaller, no tint/glow, used for Zayla's every-hit ranged auto-attack */
  playSpearThrow(fromX: number, fromY: number, toX: number, toY: number): void {
    const hasTex = this.scene.textures.exists('hero_amazon_spear_sheet');
    let proj: Phaser.GameObjects.GameObject;
    if (hasTex && this.scene.anims.exists('hero_amazon_spear_anim')) {
      const sp = this.scene.add.sprite(fromX, fromY, 'hero_amazon_spear_sheet')
        .setDisplaySize(52, 52)
        .setDepth(18).setOrigin(0.5);
      sp.play('hero_amazon_spear_anim');
      proj = sp;
    } else {
      const g = this.scene.add.graphics().setDepth(18);
      g.fillStyle(0xc8a060, 1);
      g.fillRect(-2, -1, 32, 3);
      proj = g;
    }
    const dx = toX - fromX, dy = toY - fromY;
    (proj as unknown as Phaser.GameObjects.Components.Transform).setRotation?.(Math.atan2(dy, dx));
    this.scene.tweens.add({
      targets: proj,
      x: toX, y: toY - 10,
      duration: 180,
      ease: 'Linear',
      onComplete: () => (proj as Phaser.GameObjects.Sprite).destroy?.(),
    });
  }

  /** Javelin projectile — golden spear drawn with graphics, hurled from Amazon to target (Spear Volley). */
  playJavelin(fromX: number, fromY: number, toX: number, toY: number): void {
    const startX = fromX + 30;
    const startY = fromY - 20;
    const endX = toX;
    const endY = toY - 20;
    const angle = Math.atan2(endY - startY, endX - startX);

    // Build a javelin as a Phaser.Graphics shape (tip + shaft + binding + feather tail)
    const jav = this.scene.add.graphics().setDepth(22);
    const SHAFT_LEN = 48;
    const HALF = SHAFT_LEN / 2;
    // Shadow/dark outline for contrast
    jav.fillStyle(0x2a1a04, 0.9);
    jav.fillRect(-HALF, -1.5, SHAFT_LEN, 3);
    // Wood/bronze shaft
    jav.fillStyle(0xb88848, 1);
    jav.fillRect(-HALF + 1, -1, SHAFT_LEN - 2, 2);
    // Gold leaf-shaped tip
    jav.fillStyle(0xfde68a, 1);
    jav.fillTriangle(HALF - 8, -4, HALF + 4, 0, HALF - 8, 4);
    jav.fillStyle(0xfef9c3, 1);
    jav.fillTriangle(HALF - 6, -2, HALF + 2, 0, HALF - 6, 2);
    // Red binding near tip
    jav.fillStyle(0x8b2000, 1);
    jav.fillRect(HALF - 12, -2, 3, 4);
    // Feather tail (two small triangles)
    jav.fillStyle(0xf5deb3, 0.9);
    jav.fillTriangle(-HALF, -1, -HALF - 5, -5, -HALF, 2);
    jav.fillTriangle(-HALF, 1, -HALF - 5, 5, -HALF, -2);

    jav.setPosition(startX, startY);
    jav.setRotation(angle);

    // Soft glow halo behind the spear
    const glow = this.scene.add.ellipse(startX, startY, 56, 20, 0xfbbf24, 0.35)
      .setDepth(21).setRotation(angle).setBlendMode(Phaser.BlendModes.ADD);

    // Trail sparkles every 30ms along the flight
    const trailTimer = this.scene.time.addEvent({
      delay: 30, loop: true,
      callback: () => {
        if (!jav.active) return;
        const sp = this.scene.add.circle(jav.x, jav.y, 3, 0xfef9c3, 0.8)
          .setDepth(20).setBlendMode(Phaser.BlendModes.ADD);
        this.scene.tweens.add({
          targets: sp, alpha: 0, scale: 0.1, duration: 250,
          onComplete: () => sp.destroy(),
        });
      },
    });

    // Flight
    this.scene.tweens.add({
      targets: [jav, glow],
      x: endX, y: endY,
      duration: 240,
      ease: 'Linear',
      onComplete: () => {
        trailTimer.remove(false);
        jav.destroy();
        glow.destroy();
        // Stronger impact: ring flash + gold particle burst + sparkle cross
        this.expandingRing(endX, endY, 0xfef9c3, 60, 320);
        this.expandingRing(endX, endY, 0xfbbf24, 38, 280);
        this.burstParticles(endX, endY, 'projectile', 20, {
          speed: { min: 70, max: 200 },
          angle: { min: 0, max: 360 },
          scale: { start: 0.6, end: 0 },
          tint: [0xfef9c3, 0xfde68a, 0xfbbf24, 0xffffff],
          lifespan: 420,
        });
      },
    });
  }

  /** Freeze burst — multi-layer ice encasement on the target (enhanced for Zayla's Freezing Shot). */
  playFreezeHit(x: number, y: number): void {
    // Three-ring ice shock — outer shatter, mid glow, tight core
    this.expandingRing(x, y, 0xbfdbfe, 72, 520);
    this.scene.time.delayedCall(80, () => this.expandingRing(x, y, 0x60a5fa, 54, 420));
    this.scene.time.delayedCall(150, () => this.expandingRing(x, y, 0x93c5fd, 34, 320));

    // Ice crystal spikes rising around the target (drawn as rotated triangles, 4 cardinal)
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2;
      const cx = x + Math.cos(a) * 18;
      const cy = y + Math.sin(a) * 14;
      const shard = this.scene.add.triangle(cx, cy + 6, 0, 0, -4, 12, 4, 12, 0xbfdbfe, 0.85)
        .setDepth(22).setRotation(a + Math.PI / 2).setBlendMode(Phaser.BlendModes.ADD);
      this.scene.tweens.add({
        targets: shard,
        y: cy - 8,
        alpha: 0,
        scaleY: 1.6,
        duration: 520 + i * 30,
        ease: 'Cubic.easeOut',
        onComplete: () => shard.destroy(),
      });
    }

    // Snowflake sprite at ground if available
    if (this.scene.textures.exists('effect_snowflake') && this.scene.anims.exists('fx_snowflake')) {
      const snow = this.scene.add.sprite(x, y + 20, 'effect_snowflake')
        .setDepth(22).setDisplaySize(110, 110).setOrigin(0.5, 0.5).setAlpha(0.85);
      snow.play('fx_snowflake');
      snow.once('animationcomplete', () => snow.destroy());
    }

    // Lingering frost mist — slow drifting particles
    this.burstParticles(x, y - 4, 'projectile', 16, {
      speed: { min: 20, max: 85 },
      angle: { min: 0, max: 360 },
      scale: { start: 0.45, end: 0 },
      tint: [0xdbeafe, 0xbfdbfe, 0x93c5fd, 0x60a5fa, 0xffffff],
      lifespan: 700,
    });
    // Rising frost breath — cold vapor wisps drifting upward
    this.burstParticles(x, y + 8, 'projectile', 6, {
      speed: { min: 12, max: 38 },
      angle: { min: 240, max: 300 },
      scale: { start: 0.35, end: 0 },
      tint: [0xdbeafe, 0xbfdbfe, 0xffffff],
      lifespan: 900,
      gravityY: -40,
    });
  }

  /** War Cry aura — golden ring expanding from caster through all allies */
  playWarCry(x: number, y: number): void {
    this.expandingRing(x, y, 0xfbbf24, 120, 600);
    this.burstParticles(x, y, 'projectile', 16, {
      speed: { min: 50, max: 130 },
      angle: { min: 0, max: 360 },
      scale: { start: 0.5, end: 0 },
      tint: [0xfbbf24, 0xf97316, 0xfef08a],
      lifespan: 500,
    });
  }

  playShadowTeleportIn(x: number, y: number): void {
    if (!this.scene.textures.exists('effect_shadow_teleport_in')) return;
    const sprite = this.scene.add.sprite(x, y, 'effect_shadow_teleport_in')
      .setDepth(28).setDisplaySize(80, 80).setOrigin(0.5, 0.7).setTint(0x4090c0);
    if (this.scene.anims.exists('fx_shadow_teleport_in')) {
      sprite.play('fx_shadow_teleport_in');
      sprite.once('animationcomplete', () => sprite.destroy());
    } else {
      this.scene.tweens.add({ targets: sprite, alpha: 0, duration: 300, onComplete: () => sprite.destroy() });
    }
    // Purple shimmer particles
    this.burstParticles(x, y - 10, 'projectile', 10, {
      speed: { min: 40, max: 100 },
      angle: { min: 0, max: 360 },
      scale: { start: 0.4, end: 0 },
      tint: [0x2060a0, 0x4090c0, 0x1a4878],
      lifespan: 320,
    });
  }

  playShadowTeleportOut(x: number, y: number): void {
    if (!this.scene.textures.exists('effect_shadow_teleport_out')) return;
    const sprite = this.scene.add.sprite(x, y, 'effect_shadow_teleport_out')
      .setDepth(28).setDisplaySize(80, 80).setOrigin(0.5, 0.7).setTint(0x1a4878);
    if (this.scene.anims.exists('fx_shadow_teleport_out')) {
      sprite.play('fx_shadow_teleport_out');
      sprite.once('animationcomplete', () => sprite.destroy());
    } else {
      this.scene.tweens.add({ targets: sprite, alpha: 0, duration: 300, onComplete: () => sprite.destroy() });
    }
    // Dark dissipating particles
    this.burstParticles(x, y - 10, 'projectile', 8, {
      speed: { min: 30, max: 80 },
      angle: { min: 0, max: 360 },
      scale: { start: 0.35, end: 0 },
      tint: [0x581c87, 0x4c1d95, 0x1e1b4b],
      lifespan: 280,
    });
  }

  /** Ground Slam impact — shockwave at hero feet (magic 7, 8f 72×72) */
  playGroundSlamStrike(x: number, y: number): void {
    this.playFxSprite(x, y, 'effect_ground_slam', 'fx_ground_slam', 90, 1.0);
    this.scene.cameras.main.shake(200, 0.012);
    this.expandingRing(x, y, 0xf97316, 55, 350);
    this.burstParticles(x, y, 'projectile', 12, {
      speed: { min: 50, max: 140 },
      angle: { min: 0, max: 360 },
      scale: { start: 0.55, end: 0 },
      tint: [0xf97316, 0xef4444, 0xfbbf24],
      lifespan: 380,
    });
  }

  /** Necro minion spawn — dark summoning circle + rising purple wisps + soul shards. */
  playNecroSmoke(x: number, y: number): void {
    // Ground summoning circle
    const groundRing = this.scene.add.ellipse(x, y, 60, 20, 0x3b0764, 0.7)
      .setDepth(1.8).setStrokeStyle(1, 0xa855f7, 0.8);
    this.scene.tweens.add({
      targets: groundRing, alpha: 0, scaleX: 1.6, scaleY: 1.6,
      duration: 550, ease: 'Cubic.easeOut',
      onComplete: () => groundRing.destroy(),
    });
    // Expanding violet ring
    this.expandingRing(x, y, 0x7c3aed, 42, 400);
    // Rising dark smoke wisps
    this.burstParticles(x, y, 'projectile', 12, {
      speed: { min: 15, max: 45 },
      angle: { min: 240, max: 300 },
      scale: { start: 0.45, end: 0 },
      tint: [0x1a0030, 0x3a0060, 0x4a0080, 0x2a1040, 0x6b21a8],
      lifespan: 800,
      alpha: { start: 0.75, end: 0 },
      gravityY: -80,
    });
    // Small soul shards — white-violet sparkles shooting up briefly
    this.burstParticles(x, y - 4, 'projectile', 6, {
      speed: { min: 40, max: 90 },
      angle: { min: 250, max: 290 },
      scale: { start: 0.22, end: 0 },
      tint: [0xe9d5ff, 0xa855f7, 0xffffff],
      lifespan: 500,
      gravityY: -120,
    });
  }

  /** HoT/Bless — gold-green divine aura: descending light rays, healing motes, rising halo + cross glyph. */
  playHoTGlow(x: number, y: number): void {
    // Warm golden halo flash
    const halo = this.scene.add.circle(x, y - 18, 32, 0xfde68a, 0.8).setDepth(18).setBlendMode(Phaser.BlendModes.ADD);
    this.scene.tweens.add({
      targets: halo, scale: 2.0, alpha: 0, duration: 520, ease: 'Cubic.easeOut',
      onComplete: () => halo.destroy(),
    });
    // Descending light column from above — draws a thin vertical beam that fades
    const beam = this.scene.add.rectangle(x, y - 80, 22, 120, 0xfef3c7, 0.55)
      .setDepth(17).setOrigin(0.5, 0).setBlendMode(Phaser.BlendModes.ADD);
    this.scene.tweens.add({
      targets: beam, alpha: 0, scaleX: 0.3, duration: 780, ease: 'Cubic.easeIn',
      onComplete: () => beam.destroy(),
    });
    // Descending golden motes — gravity pulls them down onto the target
    this.burstParticles(x, y - 90, 'projectile', 12, {
      speed: { min: 20, max: 60 },
      angle: { min: 70, max: 110 },
      scale: { start: 0.55, end: 0 },
      tint: [0xfde68a, 0xfef3c7, 0xfbbf24, 0xffffff],
      lifespan: 900,
      gravityY: 140,
    });
    // Rising healing sparkles (green/gold) around the target
    this.burstParticles(x, y + 6, 'projectile', 14, {
      speed: { min: 30, max: 80 },
      angle: { min: 230, max: 310 },
      scale: { start: 0.5, end: 0 },
      tint: [0x86efac, 0xbbf7d0, 0xfde68a, 0xffffff],
      lifespan: 1100,
      gravityY: -80,
    });
    // Rising cross glyph above the target — signals the divine buff
    const cross = this.scene.add.text(x, y - 14, '✛', {
      fontSize: '28px', fontFamily: 'Palatino Linotype, Georgia, serif', fontStyle: 'bold',
      color: '#fde68a', stroke: '#3a2810', strokeThickness: 4,
    }).setOrigin(0.5).setDepth(22).setAlpha(0).setScale(0.6);
    this.scene.tweens.add({
      targets: cross,
      alpha: { from: 0.95, to: 0 },
      y: y - 70,
      scale: { from: 0.95, to: 1.45 },
      duration: 900,
      ease: 'Cubic.easeOut',
      onStart: () => cross.setAlpha(0.95),
      onComplete: () => cross.destroy(),
    });
  }

  /** Druid Thornlash — whipping vines strike the target: green slash arc + thorn burst + bleeding leaves. */
  playThornlash(fromX: number, fromY: number, toX: number, toY: number): void {
    // Slash arc — animated green line from caster to target
    const g = this.scene.add.graphics().setDepth(20);
    g.lineStyle(4, 0x4ade80, 0.85);
    g.beginPath();
    g.moveTo(fromX, fromY);
    g.lineTo(toX, toY);
    g.strokePath();
    this.scene.tweens.add({ targets: g, alpha: 0, duration: 220, onComplete: () => g.destroy() });
    // Core strike flash on target
    const flash = this.scene.add.circle(toX, toY, 22, 0x86efac, 0.8).setDepth(20).setBlendMode(Phaser.BlendModes.ADD);
    this.scene.tweens.add({ targets: flash, scale: 2.4, alpha: 0, duration: 380, ease: 'Cubic.easeOut', onComplete: () => flash.destroy() });
    // Two green rings on impact
    this.expandingRing(toX, toY, 0x15803d, 44, 380);
    this.scene.time.delayedCall(80, () => this.expandingRing(toX, toY, 0x4ade80, 32, 320));
    // Thorn shards flying outward
    this.burstParticles(toX, toY, 'projectile', 14, {
      speed: { min: 70, max: 170 },
      angle: { min: 0, max: 360 },
      scale: { start: 0.55, end: 0 },
      tint: [0x15803d, 0x4ade80, 0x86efac, 0xbbf7d0],
      lifespan: 620,
      gravityY: 40,
    });
    // Falling leaves (gentle gravity) — lingering bleed marker
    this.burstParticles(toX, toY - 10, 'projectile', 6, {
      speed: { min: 20, max: 60 },
      angle: { min: 250, max: 290 },
      scale: { start: 0.45, end: 0 },
      tint: [0x16a34a, 0x4ade80],
      lifespan: 900,
      gravityY: 90,
    });
  }

  /** Druid Wild Growth — thorned roots erupt around the caster: expanding vine ring + thorn spray on all directions. */
  /** Druid (Thorne) auto-attack — Combat FX 1.1 row 12 green swipe played on target. */
  playDruidAutoswipe(x: number, y: number): void {
    if (this.scene.anims.exists('fx_druid_autoswipe')) {
      const s = this.scene.add.sprite(x, y, 'effect_combat_fx')
        .setDisplaySize(84, 84).setDepth(20).setTint(0x4ade80);
      s.play('fx_druid_autoswipe');
      s.once('animationcomplete', () => s.destroy());
    }
  }

  /** Druid Wild Growth — ground-burst root spell. Ring of verdant energy expands from the caster,
   *  thorny shoots stab up from the dirt around the AoE border, and leafy particles lift into the air.
   *  Pairs with the 2s root debuff applied by the ability handler. */
  playWildGrowth(fromX: number, fromY: number, radius: number = 220): void {
    // Expanding moss-green ring that defines the AoE border
    this.expandingRing(fromX, fromY, 0x4ade80, radius, 560);
    this.scene.time.delayedCall(140, () => this.expandingRing(fromX, fromY, 0x15803d, radius * 0.85, 520));

    // Soft inner bloom — short bright circle to sell the cast moment
    const bloom = this.scene.add.circle(fromX, fromY, 40, 0x86efac, 0.8)
      .setDepth(19).setBlendMode(Phaser.BlendModes.ADD);
    this.scene.tweens.add({
      targets: bloom, scale: { from: 0.4, to: 3.2 }, alpha: { from: 0.85, to: 0 },
      duration: 560, ease: 'Cubic.easeOut',
      onComplete: () => bloom.destroy(),
    });

    // Thorn shoots stabbing up at eight points around the AoE perimeter
    const shootCount = 8;
    for (let i = 0; i < shootCount; i++) {
      const angle = (i / shootCount) * Math.PI * 2;
      const sx = fromX + Math.cos(angle) * radius * 0.75;
      const sy = fromY + Math.sin(angle) * radius * 0.75;
      const delay = 60 + Math.random() * 180;
      this.scene.time.delayedCall(delay, () => {
        const thorn = this.scene.add.graphics().setDepth(20);
        thorn.fillStyle(0x166534, 1);
        thorn.fillTriangle(0, 0, -5, 22, 5, 22);
        thorn.lineStyle(1, 0x052e16, 1);
        thorn.strokeTriangle(0, 0, -5, 22, 5, 22);
        thorn.setPosition(sx, sy + 12);
        thorn.setAlpha(0);
        this.scene.tweens.add({
          targets: thorn, y: sy - 6, alpha: { from: 1, to: 0 }, duration: 620, ease: 'Cubic.easeOut',
          onStart: () => thorn.setAlpha(1),
          onComplete: () => thorn.destroy(),
        });
      });
    }

    // Leaf confetti drifting upward from the cast point
    this.burstParticles(fromX, fromY + 4, 'projectile', 18, {
      speed: { min: 40, max: 120 },
      angle: { min: 200, max: 340 },
      scale: { start: 0.6, end: 0 },
      tint: [0x22c55e, 0x166534, 0x86efac, 0x4ade80],
      lifespan: 900,
      gravityY: -60,
    });
  }

  /** Per-target root-binding visual used by Wild Growth. Vines coil up around the
   *  enemy's feet, hold for the root duration, then retract. */
  playRootBinding(x: number, y: number, duration: number = 2000): void {
    const group = this.scene.add.graphics().setDepth(20);
    // 4 vines at roughly cardinal positions + 2 diagonals for density
    const vineCount = 5;
    const spread = 18;
    const offsets = [
      { ox: -spread,       curl:  0.8 },
      { ox: -spread * 0.4, curl: -0.6 },
      { ox: 0,             curl:  0.0 },
      { ox:  spread * 0.4, curl:  0.6 },
      { ox:  spread,       curl: -0.8 },
    ];

    const drawVines = (progress: number, fade: number): void => {
      group.clear();
      const height = 26 * progress;
      for (let i = 0; i < vineCount; i++) {
        const o = offsets[i];
        const baseX = x + o.ox;
        const baseY = y - 2;
        const tipX  = baseX + o.curl * 6;
        const tipY  = baseY - height;
        // Dark vine stalk — thick stroke
        group.lineStyle(3, 0x14532d, 0.92 * fade);
        group.beginPath();
        group.moveTo(baseX, baseY);
        const ctrlX = baseX + o.curl * 10;
        const ctrlY = baseY - height * 0.55;
        group.lineTo(ctrlX, ctrlY);
        group.lineTo(tipX, tipY);
        group.strokePath();
        // Lighter green outline accent to read against dark enemies
        group.lineStyle(1, 0x4ade80, 0.75 * fade);
        group.beginPath();
        group.moveTo(baseX, baseY);
        group.lineTo(ctrlX, ctrlY);
        group.lineTo(tipX, tipY);
        group.strokePath();
        // Leaf/thorn at the tip
        if (progress > 0.7) {
          group.fillStyle(0x166534, 0.9 * fade);
          group.fillTriangle(tipX, tipY - 4, tipX - 3, tipY + 1, tipX + 3, tipY + 1);
          group.fillStyle(0x22c55e, 0.8 * fade);
          group.fillTriangle(tipX, tipY - 2, tipX - 2, tipY + 1, tipX + 2, tipY + 1);
        }
      }
    };

    // Phase 1: vines shoot up over 220ms
    const growState = { t: 0 };
    this.scene.tweens.add({
      targets: growState, t: 1, duration: 220, ease: 'Back.easeOut',
      onUpdate: () => drawVines(growState.t, 1),
    });
    // Phase 2: hold + fade over the remaining duration
    const holdMs = Math.max(0, duration - 220);
    this.scene.time.delayedCall(220, () => {
      const fadeState = { f: 1 };
      this.scene.tweens.add({
        targets: fadeState, f: 0, duration: holdMs, ease: 'Sine.easeIn',
        onUpdate: () => drawVines(1, fadeState.f),
        onComplete: () => group.destroy(),
      });
    });
  }

  /** Lootfi Stone Form — slabs of rock erupt around the dwarf, brown dust cloud, two stony shockwaves. */
  playStoneForm(x: number, y: number): void {
    // Ground dust puff under feet
    const dust = this.scene.add.ellipse(x, y + 4, 120, 34, 0x78716c, 0.8).setDepth(15);
    this.scene.tweens.add({
      targets: dust, scaleX: 1.6, scaleY: 1.6, alpha: 0, duration: 820, ease: 'Cubic.easeOut',
      onComplete: () => dust.destroy(),
    });
    // Bright grey flash — rock crunching into form
    const flash = this.scene.add.circle(x, y - 24, 42, 0xdcd6c8, 0.75).setDepth(18).setBlendMode(Phaser.BlendModes.ADD);
    this.scene.tweens.add({
      targets: flash, scale: 2.0, alpha: 0, duration: 520, ease: 'Cubic.easeOut',
      onComplete: () => flash.destroy(),
    });
    // Two stony shockwaves — dark outer, pale inner
    this.expandingRing(x, y, 0x44403a, 80, 620);
    this.scene.time.delayedCall(100, () => this.expandingRing(x, y, 0x9a9389, 60, 500));

    // Six jagged stone shards rising from the ground around the hero
    for (let k = 0; k < 6; k++) {
      const a = (k / 6) * Math.PI * 2;
      const startX = x + Math.cos(a) * 22;
      const startY = y + 6 + Math.sin(a) * 12;
      const endY   = startY - 18 - Math.random() * 10;
      const shard = this.scene.add.graphics().setDepth(19);
      shard.fillStyle(0x6a625a, 1);
      shard.fillTriangle(0, 0, -5, 16, 5, 16);
      shard.lineStyle(1, 0x2a2620, 1);
      shard.strokeTriangle(0, 0, -5, 16, 5, 16);
      shard.setPosition(startX, startY).setAlpha(0);
      this.scene.tweens.add({
        targets: shard, y: endY, alpha: { from: 1, to: 0 }, duration: 720, ease: 'Cubic.easeOut',
        onStart: () => shard.setAlpha(1),
        onComplete: () => shard.destroy(),
      });
    }
    // Dust / rubble particle spray
    this.burstParticles(x, y + 2, 'projectile', 18, {
      speed: { min: 60, max: 170 },
      angle: { min: 200, max: 340 },
      scale: { start: 0.65, end: 0 },
      tint: [0x3a3630, 0x6a625a, 0x9a9389, 0xd0c8b4],
      lifespan: 720,
      gravityY: 180,
    });
    // Upward grit (slower drift)
    this.burstParticles(x, y + 8, 'projectile', 10, {
      speed: { min: 30, max: 80 },
      angle: { min: 230, max: 310 },
      scale: { start: 0.5, end: 0 },
      tint: [0x9a9389, 0xd0c8b4],
      lifespan: 900,
      gravityY: -80,
    });
    // Rising anvil-ish glyph above the caster
    const glyph = this.scene.add.text(x, y - 14, '⛰', {
      fontSize: '30px', fontFamily: 'Palatino Linotype, Georgia, serif', fontStyle: 'bold',
      color: '#dcd6c8', stroke: '#1a1814', strokeThickness: 4,
    }).setOrigin(0.5).setDepth(22).setAlpha(0).setScale(0.5);
    this.scene.tweens.add({
      targets: glyph,
      alpha: { from: 0.95, to: 0 },
      y: y - 70,
      scale: { from: 1, to: 1.55 },
      duration: 860,
      ease: 'Cubic.easeOut',
      onStart: () => glyph.setAlpha(0.95),
      onComplete: () => glyph.destroy(),
    });
  }

  /** Druid Bear Form — transformation: earth shockwave, tan/green aura, rising claw glyph. */
  playBearForm(x: number, y: number): void {
    // Ground slam ellipse
    const dust = this.scene.add.ellipse(x, y + 6, 110, 32, 0x8b5a2b, 0.75).setDepth(16);
    this.scene.tweens.add({ targets: dust, scaleX: 1.6, scaleY: 1.6, alpha: 0, duration: 780, ease: 'Cubic.easeOut', onComplete: () => dust.destroy() });
    // Green/brown flash
    const flash = this.scene.add.circle(x, y - 20, 46, 0x86efac, 0.75).setDepth(19).setBlendMode(Phaser.BlendModes.ADD);
    this.scene.tweens.add({ targets: flash, scale: 2.4, alpha: 0, duration: 520, ease: 'Cubic.easeOut', onComplete: () => flash.destroy() });
    // Staggered shock rings — brown then green
    this.expandingRing(x, y, 0x6b4a20, 80, 560);
    this.scene.time.delayedCall(110, () => this.expandingRing(x, y, 0x15803d, 64, 460));
    this.scene.time.delayedCall(220, () => this.expandingRing(x, y, 0x86efac, 46, 380));
    // Dust/earth particles flying outward at foot level
    this.burstParticles(x, y + 4, 'projectile', 16, {
      speed: { min: 70, max: 180 },
      angle: { min: 200, max: 340 },
      scale: { start: 0.7, end: 0 },
      tint: [0x3a2410, 0x6b4a20, 0x8b5a2b, 0xcfa05a],
      lifespan: 680,
      gravityY: 140,
    });
    // Upward green nature aura
    this.burstParticles(x, y + 10, 'projectile', 14, {
      speed: { min: 30, max: 90 },
      angle: { min: 240, max: 300 },
      scale: { start: 0.55, end: 0 },
      tint: [0x16a34a, 0x4ade80, 0x86efac],
      lifespan: 900,
      gravityY: -100,
    });
    // Rising claw glyph over the caster
    const claw = this.scene.add.text(x, y - 30, '☠', {
      fontSize: '30px', fontFamily: 'Palatino Linotype, Georgia, serif', fontStyle: 'bold',
      color: '#a87040', stroke: '#2a1000', strokeThickness: 4,
    }).setOrigin(0.5).setDepth(22).setAlpha(0).setScale(0.5);
    this.scene.tweens.add({
      targets: claw,
      alpha: { from: 0.95, to: 0 },
      y: y - 90,
      scale: { from: 1.0, to: 1.55 },
      duration: 900,
      ease: 'Cubic.easeOut',
      onStart: () => claw.setAlpha(0.95),
      onComplete: () => claw.destroy(),
    });
  }

  /** Morveth Corpse Explosion — skeleton minion detonates: purple shockwave + gore spray + scattering bones + skull puff.
   *  Use at the minion's center on death. */
  playCorpseExplosion(x: number, y: number): void {
    // Ground blood pool
    const blood = this.scene.add.ellipse(x, y + 4, 90, 28, 0x5a0020, 0.85).setDepth(16);
    this.scene.tweens.add({
      targets: blood, scaleX: 1.5, scaleY: 1.5, alpha: 0, duration: 780, ease: 'Cubic.easeOut',
      onComplete: () => blood.destroy(),
    });
    // Violet flash — the necrotic detonation burst
    const flash = this.scene.add.circle(x, y - 4, 50, 0xa855f7, 0.8).setDepth(19).setBlendMode(Phaser.BlendModes.ADD);
    this.scene.tweens.add({
      targets: flash, scale: 2.6, alpha: 0, duration: 520, ease: 'Cubic.easeOut',
      onComplete: () => flash.destroy(),
    });
    // Inner magenta core
    const core = this.scene.add.circle(x, y - 4, 24, 0xff40a0, 0.9).setDepth(19.1).setBlendMode(Phaser.BlendModes.ADD);
    this.scene.tweens.add({
      targets: core, scale: 2.0, alpha: 0, duration: 380, ease: 'Cubic.easeOut',
      onComplete: () => core.destroy(),
    });
    // Three staggered rings — inner dark, mid violet, outer pale purple
    this.expandingRing(x, y, 0x2a0040, 90, 560);
    this.scene.time.delayedCall(80,  () => this.expandingRing(x, y, 0x6b21a8, 74, 480));
    this.scene.time.delayedCall(160, () => this.expandingRing(x, y, 0xc084fc, 56, 400));

    // Blood/gore spray outward — fast dark red motes
    this.burstParticles(x, y, 'projectile', 20, {
      speed: { min: 100, max: 240 },
      angle: { min: 0, max: 360 },
      scale: { start: 0.7, end: 0 },
      tint: [0x8b0020, 0xc0104a, 0xff3060, 0x5a0020],
      lifespan: 560,
      gravityY: 140,
    });

    // Bone shards flying out — pale sharp fragments with gravity
    this.burstParticles(x, y, 'projectile', 14, {
      speed: { min: 80, max: 210 },
      angle: { min: 210, max: 330 },
      scale: { start: 0.6, end: 0 },
      tint: [0xf5efdc, 0xe0d8b8, 0xffffff, 0xc8b890],
      lifespan: 820,
      gravityY: 240,
    });

    // Upward violet smoke pillar
    this.burstParticles(x, y - 10, 'projectile', 18, {
      speed: { min: 50, max: 140 },
      angle: { min: 240, max: 300 },
      scale: { start: 0.75, end: 0 },
      tint: [0x2a0040, 0x6b21a8, 0xa855f7, 0xc084fc],
      lifespan: 900,
      gravityY: -160,
    });

    // Four rotating bone fragments — cartoon-y but readable
    for (let k = 0; k < 4; k++) {
      const a = (k / 4) * Math.PI * 2 + Math.PI / 4;
      const bone = this.scene.add.text(x, y, '🦴', {
        fontSize: '14px', fontFamily: 'Palatino Linotype, Georgia, serif', fontStyle: 'bold',
        color: '#f5efdc', stroke: '#2a0040', strokeThickness: 2,
      }).setOrigin(0.5).setDepth(22).setAlpha(0.9);
      const dx = Math.cos(a) * 90;
      const dy = Math.sin(a) * 70 - 30;
      this.scene.tweens.add({
        targets: bone,
        x: x + dx, y: y + dy,
        rotation: (Math.random() - 0.5) * 6,
        alpha: 0,
        scale: 1.3,
        duration: 700,
        ease: 'Cubic.easeOut',
        onComplete: () => bone.destroy(),
      });
    }

    // Ascending skull marker
    const skull = this.scene.add.text(x, y - 14, '☠', {
      fontSize: '32px', fontFamily: 'Palatino Linotype, Georgia, serif', fontStyle: 'bold',
      color: '#c084fc', stroke: '#1a0030', strokeThickness: 4,
    }).setOrigin(0.5).setDepth(23).setAlpha(0).setScale(0.5);
    this.scene.tweens.add({
      targets: skull,
      alpha: { from: 1, to: 0 },
      y: y - 80,
      scale: { from: 1.0, to: 1.7 },
      duration: 900,
      ease: 'Cubic.easeOut',
      onStart: () => skull.setAlpha(1),
      onComplete: () => skull.destroy(),
    });
  }

  /** Morveth Bone Shield — skeletal bones swirl up from the ground and form a protective ring around the hero.
   *  Rotating bone glyphs + purple necrotic aura + rising skull count above head. */
  playBoneShield(x: number, y: number): void {
    // Ground burst at the hero's feet — dust + bone fragments
    const groundFlash = this.scene.add.ellipse(x, y + 2, 96, 30, 0x9370b0, 0.6).setDepth(16).setBlendMode(Phaser.BlendModes.ADD);
    this.scene.tweens.add({
      targets: groundFlash, scaleX: 1.4, scaleY: 1.4, alpha: 0, duration: 520, ease: 'Cubic.easeOut',
      onComplete: () => groundFlash.destroy(),
    });
    this.burstParticles(x, y + 4, 'projectile', 16, {
      speed: { min: 50, max: 140 },
      angle: { min: 210, max: 330 },
      scale: { start: 0.55, end: 0 },
      tint: [0xe0d8b8, 0xfef3c7, 0xc084fc, 0xa5a5a5],
      lifespan: 620,
      gravityY: -40,
    });
    // Two staggered violet shockwave rings
    this.expandingRing(x, y, 0x6b21a8, 70, 520);
    this.scene.time.delayedCall(120, () => this.expandingRing(x, y, 0xc084fc, 54, 420));

    // Rotating bone glyph ring — six bones orbiting the caster for ~1.6s
    const orbitDuration = 1600;
    const bones = ['☠', '🦴', '☠', '🦴', '☠', '🦴']; // fallback if unicode skull renders oddly, text-style glyph
    for (let k = 0; k < 6; k++) {
      const baseAngle = (k / 6) * Math.PI * 2 - Math.PI / 2;
      const bone = this.scene.add.text(x, y, bones[k], {
        fontSize: '16px', fontFamily: 'Palatino Linotype, Georgia, serif', fontStyle: 'bold',
        color: '#f5efdc', stroke: '#2a0f30', strokeThickness: 3,
      }).setOrigin(0.5).setDepth(20).setAlpha(0);
      const state = { t: 0 };
      this.scene.tweens.add({
        targets: state, t: 1, duration: orbitDuration, ease: 'Linear',
        onStart: () => bone.setAlpha(0.95),
        onUpdate: () => {
          const a = baseAngle + state.t * Math.PI * 2;          // one full revolution
          bone.x = x + Math.cos(a) * 44;
          bone.y = y - 10 + Math.sin(a) * 22;                    // flatten for waist-level orbit
          bone.rotation = a + Math.PI / 2;
          bone.alpha = 0.95 * (state.t < 0.8 ? 1 : (1 - state.t) * 5);
          bone.scale = 0.9 + Math.sin(state.t * Math.PI * 2) * 0.15;
        },
        onComplete: () => bone.destroy(),
      });
    }
    // Wispy violet smoke rising from the base ring
    this.burstParticles(x, y, 'projectile', 14, {
      speed: { min: 30, max: 90 },
      angle: { min: 230, max: 310 },
      scale: { start: 0.55, end: 0 },
      tint: [0x6b21a8, 0xa855f7, 0xc084fc, 0xe9d5ff],
      lifespan: 900,
      gravityY: -90,
    });
    // Rising skull glyph (marker)
    const mark = this.scene.add.text(x, y - 20, '🦴', {
      fontSize: '28px', fontFamily: 'Palatino Linotype, Georgia, serif', fontStyle: 'bold',
      color: '#f5efdc', stroke: '#3a0a50', strokeThickness: 4,
    }).setOrigin(0.5).setDepth(22).setAlpha(0).setScale(0.5);
    this.scene.tweens.add({
      targets: mark,
      alpha: { from: 0.95, to: 0 },
      y: y - 78,
      scale: { from: 1, to: 1.5 },
      duration: 900,
      ease: 'Cubic.easeOut',
      onStart: () => mark.setAlpha(0.95),
      onComplete: () => mark.destroy(),
    });
  }

  /** Morveth Lich Form — full-body transformation: dark tornado, red-eyed skull ascending, violet shockwaves.
   *  Played once when Lich Form activates. */
  playLichForm(x: number, y: number): void {
    // Ground-level violet shock
    const groundFlash = this.scene.add.ellipse(x, y + 2, 100, 32, 0x2a0040, 0.8).setDepth(16);
    this.scene.tweens.add({
      targets: groundFlash, scaleX: 1.6, scaleY: 1.6, alpha: 0, duration: 780, ease: 'Cubic.easeOut',
      onComplete: () => groundFlash.destroy(),
    });
    // Bright violet flash (additive, huge)
    const flash = this.scene.add.circle(x, y - 30, 56, 0xa855f7, 0.7).setDepth(19).setBlendMode(Phaser.BlendModes.ADD);
    this.scene.tweens.add({
      targets: flash, scale: 2.6, alpha: 0, duration: 620, ease: 'Cubic.easeOut',
      onComplete: () => flash.destroy(),
    });
    // Three staggered shock rings
    this.expandingRing(x, y, 0x1a0030, 100, 720);
    this.scene.time.delayedCall(100, () => this.expandingRing(x, y, 0x6b21a8, 80, 620));
    this.scene.time.delayedCall(220, () => this.expandingRing(x, y, 0xa855f7, 58, 500));
    this.scene.time.delayedCall(340, () => this.expandingRing(x, y, 0xc084fc, 40, 400));

    // Dark tornado — 3 orbiting violet wisps spiraling upward around the hero
    for (let k = 0; k < 3; k++) {
      const baseAngle = (k / 3) * Math.PI * 2;
      const wisp = this.scene.add.circle(x, y, 10, 0x6b21a8, 0.85).setDepth(20).setBlendMode(Phaser.BlendModes.ADD);
      const state = { t: 0 };
      this.scene.tweens.add({
        targets: state, t: 1, duration: 1200, ease: 'Cubic.easeInOut',
        onUpdate: () => {
          const a = baseAngle + state.t * Math.PI * 5;            // 2.5 revolutions
          const r = 40 * (1 - state.t * 0.5);                     // tightens inward
          wisp.x = x + Math.cos(a) * r;
          wisp.y = y - 10 - state.t * 80 + Math.sin(a) * 18;      // rises up
          wisp.alpha = 0.85 * (1 - state.t);
          wisp.scale = 1 + state.t * 1.5;
        },
        onComplete: () => wisp.destroy(),
      });
    }

    // Rising dark smoke column (slow, heavy)
    this.burstParticles(x, y + 10, 'projectile', 22, {
      speed: { min: 50, max: 140 },
      angle: { min: 240, max: 300 },
      scale: { start: 0.85, end: 0 },
      tint: [0x1a0030, 0x3a0070, 0x6b21a8, 0xa855f7, 0xc084fc],
      lifespan: 1100,
      gravityY: -180,
    });
    // Ember sparks bursting outward at foot level
    this.burstParticles(x, y, 'projectile', 14, {
      speed: { min: 90, max: 200 },
      angle: { min: 0, max: 360 },
      scale: { start: 0.6, end: 0 },
      tint: [0xa855f7, 0xc084fc, 0xff40a0, 0xffffff],
      lifespan: 620,
    });

    // Two red glowing eyes that flash at chest level
    for (const dx of [-10, 10]) {
      const eye = this.scene.add.circle(x + dx, y - 48, 3, 0xff2060, 1)
        .setDepth(23).setBlendMode(Phaser.BlendModes.ADD);
      this.scene.tweens.add({
        targets: eye, alpha: 0, scale: 3.2, duration: 560, ease: 'Cubic.easeOut',
        onComplete: () => eye.destroy(),
      });
    }

    // Ascending skull glyph above the caster — bigger, scarier than usual
    const skull = this.scene.add.text(x, y - 40, '☠', {
      fontSize: '46px', fontFamily: 'Palatino Linotype, Georgia, serif', fontStyle: 'bold',
      color: '#c084fc', stroke: '#1a0030', strokeThickness: 5,
    }).setOrigin(0.5).setDepth(24).setAlpha(0).setScale(0.4);
    this.scene.tweens.add({
      targets: skull,
      alpha: { from: 1, to: 0 },
      y: y - 130,
      scale: { from: 1.1, to: 1.9 },
      duration: 1200,
      ease: 'Cubic.easeOut',
      onStart: () => skull.setAlpha(1),
      onComplete: () => skull.destroy(),
    });
  }

  /** Word of Death — dark curse: purple void circle, falling shadow runes, ominous skull above target. */
  playWordOfDeath(x: number, y: number): void {
    // Ground-level void pool (dark)
    const pool = this.scene.add.ellipse(x, y + 2, 70, 22, 0x1a0530, 0.85).setDepth(16);
    this.scene.tweens.add({
      targets: pool, scaleX: 1.4, scaleY: 1.4, alpha: 0, duration: 760,
      onComplete: () => pool.destroy(),
    });
    // Violet flash (additive, bright)
    const flash = this.scene.add.circle(x, y - 16, 28, 0x8040ff, 0.7).setDepth(17).setBlendMode(Phaser.BlendModes.ADD);
    this.scene.tweens.add({
      targets: flash, scale: 2.4, alpha: 0, duration: 460, ease: 'Cubic.easeOut',
      onComplete: () => flash.destroy(),
    });
    // Three staggered expanding dark rings — deep void then violet then pale purple
    this.expandingRing(x, y, 0x2a0840, 70, 560);
    this.scene.time.delayedCall(90,  () => this.expandingRing(x, y, 0x6020a0, 54, 460));
    this.scene.time.delayedCall(180, () => this.expandingRing(x, y, 0xa060e0, 40, 380));

    // Descending shadow runes — dark motes that fall onto the target
    this.burstParticles(x, y - 80, 'projectile', 10, {
      speed: { min: 20, max: 60 },
      angle: { min: 70, max: 110 },
      scale: { start: 0.55, end: 0 },
      tint: [0x6020a0, 0x8040ff, 0xa060e0, 0x2a0840],
      lifespan: 820,
      gravityY: 100,
    });

    // Upward wisps of shadow — curling smoke around the target's body
    this.burstParticles(x, y + 6, 'projectile', 14, {
      speed: { min: 40, max: 110 },
      angle: { min: 210, max: 330 },
      scale: { start: 0.55, end: 0 },
      tint: [0x1a0530, 0x4a10a0, 0x8040ff, 0xc080ff],
      lifespan: 900,
      gravityY: -110,
    });

    // Three rotating ember skulls circling the target head — curse marker
    for (let k = 0; k < 3; k++) {
      const baseAngle = (k / 3) * Math.PI * 2;
      const skull = this.scene.add.text(x, y - 28, '☠', {
        fontSize: '16px', fontFamily: 'Palatino Linotype, Georgia, serif', fontStyle: 'bold',
        color: '#c080ff', stroke: '#1a0530', strokeThickness: 3,
      }).setOrigin(0.5).setDepth(22).setAlpha(0.9);
      const state = { t: 0 };
      this.scene.tweens.add({
        targets: state, t: 1, duration: 900, ease: 'Linear',
        onUpdate: () => {
          const a = baseAngle + state.t * Math.PI * 3; // 1.5 revolutions
          skull.x = x + Math.cos(a) * 22;
          skull.y = y - 28 + Math.sin(a) * 12;
          skull.alpha = 0.9 * (1 - state.t * 0.7);
          skull.scale = 0.9 + Math.sin(state.t * Math.PI * 2) * 0.25;
        },
        onComplete: () => skull.destroy(),
      });
    }

    // Rising skull glyph above the target
    const mark = this.scene.add.text(x, y - 20, '☠', {
      fontSize: '32px', fontFamily: 'Palatino Linotype, Georgia, serif', fontStyle: 'bold',
      color: '#b080ff', stroke: '#1a0530', strokeThickness: 4,
    }).setOrigin(0.5).setDepth(22.1).setAlpha(0).setScale(0.5);
    this.scene.tweens.add({
      targets: mark,
      alpha: { from: 0.95, to: 0 },
      y: y - 80,
      scale: { from: 0.95, to: 1.55 },
      duration: 950,
      ease: 'Cubic.easeOut',
      onStart: () => mark.setAlpha(0.95),
      onComplete: () => mark.destroy(),
    });
  }

  // ---- Private helpers ----

  /** Generic helper: play a one-shot animated sprite at (x, y) with given display size and y-origin */
  private playFxSprite(x: number, y: number, texKey: string, animKey: string, size: number, originY: number): void {
    if (!this.scene.textures.exists(texKey)) return;
    const sprite = this.scene.add.sprite(x, y, texKey)
      .setDepth(25).setDisplaySize(size, size).setOrigin(0.5, originY);
    if (this.scene.anims.exists(animKey)) {
      sprite.play(animKey);
      sprite.once('animationcomplete', () => sprite.destroy());
    } else {
      this.scene.tweens.add({ targets: sprite, alpha: 0, duration: 300, onComplete: () => sprite.destroy() });
    }
  }

  private playEffectSprite(x: number, y: number, textureKey: string, animKey: string): void {
    const sprite = this.scene.add.sprite(x, y, textureKey).setDepth(25);
    if (this.scene.anims.exists(animKey)) {
      sprite.play(animKey);
      sprite.once('animationcomplete', () => sprite.destroy());
    } else {
      // Fallback: quick fade-out
      this.scene.tweens.add({
        targets: sprite,
        alpha: 0,
        duration: 180,
        onComplete: () => sprite.destroy(),
      });
    }
  }

  /** Thornlash claw — Combat FX 1.1 row 19 sprite played on target with a dark-red tint. */
  playThornlashClaw(x: number, y: number): void {
    if (this.scene.anims.exists('fx_thornlash_claw')) {
      const s = this.scene.add.sprite(x, y, 'effect_combat_fx')
        .setDisplaySize(96, 96).setDepth(23).setBlendMode(Phaser.BlendModes.NORMAL)
        .setTint(0x7f1d1d); // dark red recolor
      s.play('fx_thornlash_claw');
      s.once('animationcomplete', () => s.destroy());
    }
  }

  /** Moonfire — human-form Thornlash visual. Plays Combat FX 1.1 gif 23
   *  (frames 220-229) on top of the target with a cool-blue tint that
   *  reads as moonlight. Falls back to a small expanding ring if the
   *  sheet isn't loaded so the hit still registers visually. */
  playMoonfireStrike(x: number, y: number): void {
    if (this.scene.anims.exists('fx_moonfire')) {
      const s = this.scene.add.sprite(x, y - 30, 'effect_combat_fx')
        .setDisplaySize(120, 120).setDepth(24).setBlendMode(Phaser.BlendModes.ADD)
        .setTint(0x6fb8ff); // cool moonlight blue
      s.play('fx_moonfire');
      s.once('animationcomplete', () => s.destroy());
    }
    // Always: small ground halo + sparkle for depth.
    this.expandingRing(x, y - 10, 0x8bc8f0, 44, 360);
    this.burstParticles(x, y - 20, 'projectile', 6, {
      speed: { min: 30, max: 70 },
      angle: { min: 200, max: 340 },
      lifespan: 360,
      tint: 0xcfe7ff,
      scale: { start: 0.6, end: 0 },
      alpha: { start: 0.9, end: 0 },
      blendMode: Phaser.BlendModes.ADD,
    });
  }

  /** Rune Stone activation — bright shiny blue core + layered smoky glow + expanding rings. */
  playRuneStoneActivate(x: number, y: number): void {
    // 1) Bright shiny core — additive white-blue burst
    const core = this.scene.add.circle(x, y, 10, 0xeaf6ff, 1)
      .setDepth(25).setBlendMode(Phaser.BlendModes.ADD);
    this.scene.tweens.add({
      targets: core, scale: { from: 0.6, to: 3.2 }, alpha: { from: 1, to: 0 },
      duration: 720, ease: 'Cubic.easeOut',
      onComplete: () => core.destroy(),
    });
    // 2) Inner sapphire glow — secondary sheen behind the core
    const glowInner = this.scene.add.circle(x, y, 22, 0x60a8ff, 0.85)
      .setDepth(24).setBlendMode(Phaser.BlendModes.ADD);
    this.scene.tweens.add({
      targets: glowInner, scale: { from: 0.8, to: 2.2 }, alpha: { from: 0.85, to: 0 },
      duration: 900, ease: 'Cubic.easeOut',
      onComplete: () => glowInner.destroy(),
    });
    // 3) Outer hazy aura — dark blue soft halo
    const glowOuter = this.scene.add.circle(x, y, 46, 0x2060c0, 0.55)
      .setDepth(23).setBlendMode(Phaser.BlendModes.ADD);
    this.scene.tweens.add({
      targets: glowOuter, scale: { from: 0.9, to: 1.8 }, alpha: { from: 0.55, to: 0 },
      duration: 1100, ease: 'Cubic.easeOut',
      onComplete: () => glowOuter.destroy(),
    });
    // 4) Two expanding rings for a shockwave read
    this.expandingRing(x, y, 0x80c8ff, 70, 720);
    this.scene.time.delayedCall(140, () => this.expandingRing(x, y, 0x4080ff, 55, 620));
    // 5) Smoky wisps drifting up
    this.burstParticles(x, y, 'projectile', 12, {
      speed: { min: 20, max: 70 },
      angle: { min: 230, max: 310 },
      scale: { start: 0.7, end: 0 },
      tint: [0x4080ff, 0x60a8ff, 0xb0d8ff, 0xeaf6ff],
      lifespan: 1200,
      gravityY: -60,
    });
    // 6) Sideways sparkle spray
    this.burstParticles(x, y, 'projectile', 10, {
      speed: { min: 60, max: 160 },
      angle: { min: 0, max: 360 },
      scale: { start: 0.55, end: 0 },
      tint: [0xffffff, 0xeaf6ff, 0x80c8ff],
      lifespan: 600,
    });
    // 7) Rising rune glyph marker
    const glyph = this.scene.add.text(x, y - 8, 'ᛊ', {
      fontSize: '26px', fontFamily: 'Palatino Linotype, Georgia, serif', fontStyle: 'bold',
      color: '#eaf6ff', stroke: '#1040a0', strokeThickness: 3,
    }).setOrigin(0.5).setDepth(26).setAlpha(0).setScale(0.4);
    this.scene.tweens.add({
      targets: glyph,
      alpha: { from: 0.95, to: 0 },
      y: y - 60,
      scale: { from: 1, to: 1.6 },
      duration: 1000,
      ease: 'Cubic.easeOut',
      onStart: () => glyph.setAlpha(0.95),
      onComplete: () => glyph.destroy(),
    });
  }

  /** Shaman totem pulse — per-school color + secondary effect so each school reads at a glance. */
  playTotemPulse(x: number, y: number, school: 'lightning' | 'healing' | 'slowing'): void {
    if (school === 'lightning') {
      // Blue shockwave ring + 3 crackle sparks
      this.expandingRing(x, y - 20, 0x38bdf8, 56, 380);
      this.expandingRing(x, y - 20, 0x93c5fd, 28, 320);
      if (this.scene.textures.exists('spark')) {
        this.burstParticles(x, y - 24, 'spark', 6, {
          speed: { min: 40, max: 120 }, lifespan: 260, scale: { start: 0.6, end: 0 },
          tint: [0x38bdf8, 0x93c5fd], alpha: { start: 1, end: 0 },
        });
      }
    } else if (school === 'healing') {
      // Soft green dome + gentle rising motes
      this.expandingRing(x, y - 22, 0x10b981, 58, 420);
      this.expandingRing(x, y - 22, 0x6ee7b7, 32, 360);
      if (this.scene.textures.exists('spark')) {
        this.burstParticles(x, y - 16, 'spark', 6, {
          speedY: { min: -60, max: -30 }, speedX: { min: -20, max: 20 }, lifespan: 520,
          scale: { start: 0.5, end: 0 }, tint: [0x10b981, 0xa7f3d0], alpha: { start: 0.9, end: 0 },
        });
      }
    } else {
      // Earthbind — amber ground dust + low wide ring (slow pulse reads "ground")
      this.expandingRing(x, y, 0xa16207, 90, 520);
      this.expandingRing(x, y, 0xd97706, 60, 440);
      if (this.scene.textures.exists('spark')) {
        this.burstParticles(x, y - 4, 'spark', 8, {
          speed: { min: 20, max: 70 }, lifespan: 500, scale: { start: 0.7, end: 0 },
          tint: [0xa16207, 0x78350f, 0xd97706], alpha: { start: 0.9, end: 0 },
          gravityY: 40,
        });
      }
    }
  }

  /** Gilded Altar blessing burst — golden expanding ring + warm bronze
   *  spark particles centered on a hero. Rendered at depth 75 (above the
   *  altar popup's overlay at depth 70 + tier cards at depth 73) so the
   *  flash reads through the dim popup scrim that hides heroes during the
   *  altar UI. Anchored at the hero's torso (caller adjusts y for height). */
  playAltarBlessing(x: number, y: number): void {
    // Outer gold ring — wide, slow fade, reads as the "blessing accepted" moment.
    const outer = this.scene.add.graphics().setDepth(75);
    outer.lineStyle(3, 0xffd060, 0.95);
    outer.strokeCircle(0, 0, 70);
    outer.setPosition(x, y).setScale(0.05).setAlpha(0.95);
    this.scene.tweens.add({
      targets: outer, scaleX: 1, scaleY: 1, alpha: 0,
      duration: 620, ease: 'Cubic.easeOut',
      onComplete: () => outer.destroy(),
    });
    // Inner bronze ring follows ~120 ms later for a layered "double pulse" feel.
    this.scene.time.delayedCall(120, () => {
      const inner = this.scene.add.graphics().setDepth(75);
      inner.lineStyle(2, 0xc8973a, 0.85);
      inner.strokeCircle(0, 0, 50);
      inner.setPosition(x, y).setScale(0.05).setAlpha(0.85);
      this.scene.tweens.add({
        targets: inner, scaleX: 1, scaleY: 1, alpha: 0,
        duration: 480, ease: 'Cubic.easeOut',
        onComplete: () => inner.destroy(),
      });
    });
    // Burst particles — warm gold motes flying out radially. Uses 'projectile'
    // (always loaded) so the effect renders even if special textures aren't
    // available. setDepth(75) on the emitter so it stacks above the popup.
    if (this.scene.textures.exists('projectile')) {
      const emitter = this.scene.add
        .particles(x, y, 'projectile', {
          speed: { min: 90, max: 170 },
          angle: { min: 0, max: 360 },
          scale: { start: 0.7, end: 0 },
          alpha: { start: 0.95, end: 0 },
          lifespan: 520,
          tint: [0xffd060, 0xffe8a0, 0xc8973a],
          emitting: false,
        })
        .setDepth(75);
      emitter.explode(18);
      this.scene.time.delayedCall(720, () => emitter.destroy());
    }
  }

  private expandingRing(x: number, y: number, color: number, maxRadius: number, duration: number): void {
    const g = this.scene.add.graphics().setDepth(19);
    g.lineStyle(2, color, 0.9);
    g.strokeCircle(0, 0, maxRadius);
    g.setPosition(x, y).setScale(0.05).setAlpha(0.9);
    this.scene.tweens.add({
      targets: g,
      scaleX: 1,
      scaleY: 1,
      alpha: 0,
      duration,
      ease: 'Cubic.easeOut',
      onComplete: () => g.destroy(),
    });
  }

  private burstParticles(
    x: number,
    y: number,
    textureKey: string,
    count: number,
    config: Phaser.Types.GameObjects.Particles.ParticleEmitterConfig,
  ): void {
    const emitter = this.scene.add
      .particles(x, y, textureKey, { ...config, emitting: false })
      .setDepth(20);
    emitter.explode(count);
    const lifespan = typeof config.lifespan === 'number'
      ? config.lifespan
      : (config.lifespan != null && typeof (config.lifespan as { max?: number }).max === 'number')
        ? (config.lifespan as { max: number }).max
        : 500;
    this.scene.time.delayedCall(lifespan + 200, () => emitter.destroy());
  }

  // ── Valka (Valkyrie) ability VFX — uses extracted Combat FX 1.1 rows ────
  // 14 = shield bash impact (white concentric pulse)
  // 27 = whirlwind arc (gold slashes)
  // 16 = bludgeon slam (blue energy burst)

  /** Shield Bash impact — FX 14 centred on the struck enemy + expanding white
   *  ring to sell the percussive shove. Also drops a small particle burst so
   *  the moment reads as heavy even if the sheet fails to load. */
  playValkaShieldBash(x: number, y: number): void {
    this.playFxSprite(x, y, 'effect_fx_shieldbash', 'fx_shieldbash', 110, 0.5);
    this.expandingRing(x, y, 0xffffff, 80, 320);
    this.burstParticles(x, y, 'projectile', 10, {
      speed: { min: 60, max: 160 },
      angle: { min: 0, max: 360 },
      scale: { start: 0.55, end: 0 },
      tint: [0xc084fc, 0xffffff, 0xe0d0ff],
      lifespan: 320,
    });
  }

  /** Whirlwind spin — FX 1 (Combat FX pack) plays in a wider footprint around
   *  Valka for the full ~1.2 s spin window; a brief camera micro-shake sells
   *  the sustained sweep. `facingLeft` mirrors the strip horizontally so the
   *  directional gestures in the effect match Valka's swing direction. */
  playValkaWhirlwind(x: number, y: number, facingLeft = false): void {
    this.playFxSpriteFacing(x, y, 'effect_fx_whirlwind', 'fx_whirlwind', 180, 0.5, facingLeft);
    this.scene.cameras.main.shake(180, 0.0020);
  }

  /** Tempest Slam finisher — Combat-FX Preview 16 (blue energy burst) plays
   *  in the front-cone target zone, mirrored on `facingLeft` so the
   *  directional strike matches Valka's swing. Plus a hard expanding ring
   *  in the same blue tint and a camera shake. No particle burst — the
   *  sprite + ring carries the moment cleanly. */
  playValkaBludgeonSlam(x: number, y: number, facingLeft = false): void {
    this.playFxSpriteFacing(x, y, 'effect_fx_bludgeon', 'fx_bludgeon', 170, 0.5, facingLeft);
    this.expandingRing(x, y, 0x80c4ff, 100, 380);
    this.scene.cameras.main.shake(160, 0.0040);
  }

  /** Variant of `playFxSprite` that supports horizontal mirroring. The base
   *  helper doesn't expose flipX so Valka's directional VFX (whirlwind,
   *  tempest slam) re-implement the one-shot-then-destroy pattern with a
   *  setFlipX(true) when the attack was aimed at a target on Valka's left. */
  private playFxSpriteFacing(
    x: number, y: number,
    texKey: string, animKey: string,
    size: number, originY: number,
    facingLeft: boolean,
  ): void {
    if (!this.scene.textures.exists(texKey)) return;
    const sprite = this.scene.add.sprite(x, y, texKey)
      .setDepth(25).setDisplaySize(size, size).setOrigin(0.5, originY);
    if (facingLeft) sprite.setFlipX(true);
    if (this.scene.anims.exists(animKey)) {
      sprite.play(animKey);
      sprite.once('animationcomplete', () => sprite.destroy());
    } else {
      this.scene.tweens.add({ targets: sprite, alpha: 0, duration: 300, onComplete: () => sprite.destroy() });
    }
  }

  /** Banner of Valor cast shockwave — single expanding ring centred on
   *  Valka that colour-lerps from light gold (0xffe070) at centre to red
   *  (0xd63030) at the edge and fades out as it hits max radius. Replaces
   *  the old icon-pop VFX per user request. */
  playValkaBannerShockwave(x: number, y: number): void {
    const ring = this.scene.add.graphics().setDepth(10);
    const maxR = 180;
    const duration = 650;
    // sRGB components for the gold → red lerp.
    const r1 = 0xff, g1 = 0xe0, b1 = 0x70; // light gold
    const r2 = 0xd6, g2 = 0x30, b2 = 0x30; // battle red
    this.scene.tweens.addCounter({
      from: 0, to: 1, duration, ease: 'Cubic.easeOut',
      onUpdate: (tween) => {
        const t = tween.getValue() ?? 0;
        ring.clear();
        if (!ring.active) return;
        const r = t * maxR;
        const alpha = Math.max(0, 1 - t * 0.95); // fades to near-zero at the edge
        const mr = Math.round(r1 + (r2 - r1) * t);
        const mg = Math.round(g1 + (g2 - g1) * t);
        const mb = Math.round(b1 + (b2 - b1) * t);
        const color = (mr << 16) | (mg << 8) | mb;
        // Thicker outer stroke + thinner inner for a visible "wave" band.
        ring.lineStyle(5, color, alpha);
        ring.strokeCircle(x, y, r);
        if (r > 14) {
          ring.lineStyle(2, color, alpha * 0.6);
          ring.strokeCircle(x, y, r - 10);
        }
      },
      onComplete: () => ring.destroy(),
    });
  }

  /** Legacy alias kept so any other call site that still references the
   *  older purple-ring banner VFX doesn't break. Points at the new
   *  shockwave so the cast visual is consistent. */
  playValkaBanner(x: number, y: number): void {
    this.playValkaBannerShockwave(x, y);
  }

  /** Counter-Strike passive — purple spark at the attacker. `isMelee=false`
   *  means the counter was extended to a ranged attacker by talent, which
   *  gets a distinct tint (softer violet) so the player can tell which
   *  counter variant fired. */
  playValkaCounter(x: number, y: number, isMelee: boolean): void {
    const tint = isMelee ? 0xc084fc : 0xa78bfa;
    this.expandingRing(x, y, tint, 40, 240);
    this.burstParticles(x, y, 'projectile', 6, {
      speed: { min: 50, max: 120 },
      angle: { min: 0, max: 360 },
      scale: { start: 0.5, end: 0 },
      tint: [tint, 0xffffff],
      lifespan: 300,
    });
  }
}
